import { CITY_CORDON_PREVIEW_SPECIFICATION } from "./cityCordonPreviewSpecification.js";

const ACTIVE_TICKET_STATUSES = new Set(["blocked", "ready", "leased", "in_progress"]);

const pretty = (value) => String(value || "")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const text = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(text).filter(Boolean).join(" · ");
  const primary = value.summary || value.description || value.purpose || value.intent || value.notes || value.label || value.name;
  if (primary) return text(primary);
  return Object.entries(value)
    .filter(([, item]) => typeof item === "string" || typeof item === "number")
    .map(([key, item]) => `${pretty(key)} ${item}`)
    .join(" · ");
};

const layerText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  const evidence = [...list(value.exteriorEvidence), ...list(value.interiorEvidence)].map(text).filter(Boolean);
  return [text(value.purpose), evidence.length ? `Evidence: ${evidence.join("; ")}` : ""].filter(Boolean).join(" — ");
};

const list = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return Object.entries(value).map(([name, detail]) => ({ name: pretty(name), detail }));
  return [value];
};

const slug = (value, fallback) => String(value || fallback)
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "") || fallback;

function revisionNumber(revision) {
  return Number(revision?.revisionNumber ?? revision?.number ?? 0);
}

/**
 * Normalize every large-scale atlas entry for the Structures workspace.
 * `buildings` and the module/view name remain compatibility identifiers for
 * existing saved revisions; small props and materials belong in Assets.
 */
export function planStructures(body = {}) {
  const buildingSource = body.buildings ?? body.buildingFamilies ?? body.atlas?.buildings ?? body.architecture?.buildingFamilies ?? [];
  const structureSource = body.structures ?? [];
  const radialStructureSource = body.radialStructures ?? [];
  const buildingRows = (Array.isArray(buildingSource) ? buildingSource : Object.entries(buildingSource).map(([id, value]) => ({ id, ...(value || {}) })))
    .map((entry) => ({ ...entry, atlasEntryKind: "building" }));
  const structureRows = (Array.isArray(structureSource) ? structureSource : Object.entries(structureSource).map(([id, value]) => ({ id, ...(value || {}) })))
    .map((entry) => ({ ...entry, atlasEntryKind: "macro_structure" }));
  const radialStructureRows = (Array.isArray(radialStructureSource) ? radialStructureSource : Object.entries(radialStructureSource).map(([id, value]) => ({ id, ...(value || {}) })))
    .map((entry) => ({ ...entry, atlasEntryKind: "radial_structure" }));
  const rows = [...buildingRows, ...structureRows, ...radialStructureRows];
  return rows.map((building, index) => {
    const fallbackKind = building.atlasEntryKind === "building" ? "Building" : "Structure";
    const rawId = String(building.id || building.buildingId || building.buildingFamilyId || "");
    const isCanonicalCordonSegment = building.atlasEntryKind === "radial_structure" && rawId === "city_cordon_ring_segmented";
    const name = isCanonicalCordonSegment
      ? "City Cordon Segment A"
      : text(building.label || building.name || building.displayName || building.title) || `${fallbackKind} ${index + 1}`;
    const history = building.historicalLayers || building.historyLayers || building.history || {};
    const historyArray = Array.isArray(history) ? history : [];
    return {
      ...building,
      id: rawId || slug(name, `building-${index + 1}`),
      name,
      summary: isCanonicalCordonSegment
        ? "One reusable 402 m trapezoidal wall segment, ready to place as a source module. It is one twelfth of the optional cordon-ring assembly."
        : text(building.summary || building.thesis || building.purpose || building.formerPurpose),
      isCanonicalCordonSegment,
      original: layerText(building.formerPurpose || building.originalFunction || history.originalFunction || history.original || historyArray[0]),
      conversion: layerText(building.arenaConversion || history.arenaConversion || history.conversion || historyArray[1]),
      collapse: layerText(building.ongoingCollapse || building.collapse || history.ongoingCollapse || history.collapse || historyArray[2]),
      exterior: building.exterior || building.exteriorDesign || building.aesthetic || {},
      gameplay: building.gameplay || building.gameplayPlan || {},
      interior: typeof (building.interiors || building.interior) === "object" ? (building.interiors || building.interior) : { summary: building.interiors || building.interior || building.interiorDesign || "" },
      production: building.productionAssets || building.productionPlan || building.assetPlan || building.requiredAssets || {},
      visualPrompts: building.visualPrompts || {},
    };
  });
}

// Compatibility alias for integrations that imported the original workspace.
export const planBuildings = planStructures;

const memoEscape = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

function memoInline(value) {
  return memoEscape(value)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
}

/** Render the small, deliberately bounded Markdown subset returned by design agents. */
export function formatStructureMemo(value) {
  const lines = String(value ?? "").replace(/\r\n?/g, "\n").split("\n");
  const output = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index].trim();
    if (!line) { index += 1; continue; }
    const heading = /^(#{2,4})\s+(.+)$/.exec(line);
    if (heading) {
      const level = Math.min(4, Math.max(3, heading[1].length));
      output.push(`<h${level}>${memoInline(heading[2])}</h${level}>`);
      index += 1;
      continue;
    }
    const next = lines[index + 1]?.trim() || "";
    if (/^\|.+\|$/.test(line) && /^\|(?:\s*:?-{3,}:?\s*\|)+$/.test(next)) {
      const rows = [];
      const cells = (row) => row.slice(1, -1).split("|").map((cell) => memoInline(cell.trim()));
      const headers = cells(line);
      index += 2;
      while (index < lines.length && /^\|.+\|$/.test(lines[index].trim())) {
        rows.push(cells(lines[index].trim()));
        index += 1;
      }
      output.push(`<div class="building-memo-table-wrap"><table><thead><tr>${headers.map((cell) => `<th>${cell}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`);
      continue;
    }
    const bullet = /^[-*]\s+(.+)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.+)$/.exec(line);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      const items = [];
      while (index < lines.length) {
        const current = lines[index].trim();
        const match = ordered ? /^\d+[.)]\s+(.+)$/.exec(current) : /^[-*]\s+(.+)$/.exec(current);
        if (!match) break;
        items.push(`<li>${memoInline(match[1])}</li>`);
        index += 1;
      }
      output.push(`<${ordered ? "ol" : "ul"}>${items.join("")}</${ordered ? "ol" : "ul"}>`);
      continue;
    }
    output.push(`<p>${memoInline(line)}</p>`);
    index += 1;
  }
  return output.join("");
}

export function resolveStructureFromBody(body, structureId, { allowCatalogFallback = true } = {}) {
  const id = String(structureId || "");
  const planned = planStructures(body || {}).find((structure) => structure.id === id);
  if (planned || !allowCatalogFallback) return planned || null;
  const catalogSource = planStructures({ radialStructures: [CITY_CORDON_PREVIEW_SPECIFICATION] }).find((structure) => structure.id === id);
  return catalogSource ? { ...catalogSource, catalogSource: true } : null;
}

function ticketTargetsStructure(ticket, structureId) {
  const id = String(structureId || "");
  if (!id) return false;
  const spec = ticket?.spec || {};
  return [spec.structureId, spec.buildingId, spec.workId].some((value) => String(value || "") === id)
    || String(spec.instruction || "").includes(`(${id})`);
}

function resolveStructureLineageFromBody(body, structureId) {
  const id = String(structureId || "");
  const structures = planStructures(body || {});
  return structures.find((structure) => structure.id === id)
    || structures.find((structure) => structure.sourceStructureId === id)
    || null;
}

function lineageParentId(revision) {
  const validation = revision?.validation && typeof revision.validation === "object" ? revision.validation : {};
  return String(
    revision?.parentRevisionId
    ?? validation.parentRevisionId
    ?? validation.baseRevisionId
    ?? "",
  );
}

function valueText(value, suffix = "") {
  if (value == null || value === "") return "—";
  const number = Number(value);
  const shown = Number.isFinite(number) ? String(Number(number.toFixed(3))) : String(value);
  return `${shown}${suffix}`;
}

/** A deliberately small explanation of what changed, rather than a raw document diff. */
export function structureChangedFields(before = {}, after = {}) {
  const previous = before?.segment || {};
  const next = after?.segment || {};
  const fields = [];
  const changed = (key, label, suffix = " m") => {
    if (next[key] == null || previous[key] === next[key]) return;
    fields.push({ key, label, before: valueText(previous[key], suffix), after: valueText(next[key], suffix) });
  };
  changed("centrelineLengthM", "Length");
  changed("radialDepthM", "Depth");
  changed("totalHeightM", "Height");
  changed("count", "Segment count", "");

  const previousOpening = before?.longitudinalOpening || null;
  const nextOpening = after?.longitudinalOpening || null;
  const openingText = (opening) => opening
    ? `${valueText(opening.clearRadialWidthM, " m")} clear × ${valueText(opening.clearHeightM, " m")} high`
    : "none";
  if (openingText(previousOpening) !== openingText(nextOpening)) {
    fields.push({
      key: "longitudinalOpening",
      label: previousOpening && nextOpening ? "Tunnel opening" : nextOpening ? "Added tunnel" : "Removed tunnel",
      before: openingText(previousOpening),
      after: openingText(nextOpening),
    });
  }
  const roadText = (opening) => {
    const road = opening?.roadLayout;
    return road ? `${valueText(road.laneCount, " lanes")}, ${valueText(road.laneWidthM, " m each")}` : "none";
  };
  if (roadText(previousOpening) !== roadText(nextOpening)) {
    fields.push({
      key: "roadLayout",
      label: "Roads",
      before: roadText(previousOpening),
      after: roadText(nextOpening),
    });
  }
  const previousName = text(before?.label || before?.name);
  const nextName = text(after?.label || after?.name);
  if (nextName && previousName && previousName !== nextName) {
    fields.push({ key: "name", label: "Name", before: previousName, after: nextName });
  }
  return fields;
}

/**
 * Produces a source-rooted revision graph from immutable revision snapshots.
 * Explicit parent links create branches; a missing/unknown parent safely falls
 * back to SOURCE rather than inventing a relationship.
 */
export function structureRevisionLineage(revisions = [], structureId = "city_cordon_ring_segmented") {
  const rows = (Array.isArray(revisions) ? revisions : [])
    .filter((revision) => revision?.id)
    .map((revision) => ({
      revision,
      id: String(revision.id),
      number: revisionNumber(revision),
      structure: resolveStructureLineageFromBody(revision.body || {}, structureId),
    }))
    .filter((entry) => entry.structure)
    .sort((left, right) => left.number - right.number || String(left.revision.createdAt || "").localeCompare(String(right.revision.createdAt || "")));
  const byId = new Map(rows.map((entry) => [entry.id, entry]));
  const source = resolveStructureFromBody({}, structureId, { allowCatalogFallback: true }) || {};
  return rows.map((entry) => {
    const requestedParentId = lineageParentId(entry.revision);
    const parent = requestedParentId ? byId.get(requestedParentId) : null;
    const parentId = parent ? parent.id : "source";
    const changedFields = structureChangedFields(parent?.structure || source, entry.structure);
    const summaryFields = [
      ...changedFields.filter((field) => field.key === "roadLayout"),
      ...changedFields.filter((field) => field.key !== "roadLayout").slice(0, 2),
    ];
    return {
      ...entry,
      parentId,
      label: `R${String(entry.number).padStart(2, "0")}`,
      status: String(entry.revision.status || "candidate"),
      changedFields,
      changeSummary: summaryFields.length
        ? summaryFields.map((field) => `${field.label}: ${field.after}`).join(" · ")
        : "Recorded specification revision",
    };
  });
}

/** Accessible, compact source → revision tree. The caller supplies project routes. */
export function renderStructureRevisionTree(revisions = [], {
  structureId = "city_cordon_ring_segmented",
  selectedRevisionId = "",
  hrefFor = () => "#",
} = {}) {
  const nodes = structureRevisionLineage(revisions, structureId);
  const escape = memoEscape;
  const childCount = new Map();
  for (const node of nodes) childCount.set(node.parentId, (childCount.get(node.parentId) || 0) + 1);
  const sourceHref = hrefFor({ source: true });
  const sourceSelected = !selectedRevisionId;
  const nodeMarkup = (node) => {
    const selected = node.id === selectedRevisionId;
    const branching = (childCount.get(node.parentId) || 0) > 1;
    const statusLabel = String(node.status || "").toLowerCase() === "review" ? "Awaiting decision" : pretty(node.status);
    const changes = node.changedFields.length
      ? `<small>${escape(node.changeSummary)}</small>`
      : `<small>Snapshot recorded</small>`;
    return `<li class="structure-lineage-node ${selected ? "on" : ""} ${branching ? "branch" : ""}">
      <a href="${escape(hrefFor({ revisionId: node.id }))}" aria-current="${selected ? "page" : "false"}">
        <span class="structure-lineage-dot" aria-hidden="true"></span><b>${escape(node.label)}</b><em>${escape(statusLabel)}</em>${changes}
      </a>
    </li>`;
  };
  const children = (parentId) => nodes.filter((node) => node.parentId === parentId);
  const renderChildren = (parentId) => {
    const direct = children(parentId);
    if (!direct.length) return "";
    return `<ol>${direct.map((node) => `${nodeMarkup(node)}${renderChildren(node.id)}`).join("")}</ol>`;
  };
  return `<section class="structure-lineage" aria-label="Revision lineage">
    <header><span>Revision lineage</span><small>${nodes.length ? `${nodes.length} project version${nodes.length === 1 ? "" : "s"}` : "No project versions"}</small></header>
    <ol class="structure-lineage-root"><li class="structure-lineage-source ${sourceSelected ? "on" : ""}"><a href="${escape(sourceHref)}" aria-current="${sourceSelected ? "page" : "false"}"><span class="structure-lineage-dot" aria-hidden="true"></span><b>SOURCE</b><em>Repository specification</em><small>Unchanged template</small></a>${renderChildren("source")}</li></ol>
  </section>`;
}

/**
 * Deliberately route-only review choices.  The existing review screen owns the
 * actual human decision, so this cannot reject or mutate a revision by itself.
 */
export function renderStructureReviewChoices({
  revision = null,
  reviewDecision = null,
  continueHref = "",
  retryParentHref = "",
  reviewHref = "",
} = {}) {
  if (!revision) return "";
  const status = String(revision.status || "candidate").toLowerCase();
  const decision = String(reviewDecision?.decision || "").toLowerCase();
  const hasRequestedChanges = status === "rejected" && decision === "change_requested";
  const parentLabel = lineageParentId(revision) ? "its parent version" : "SOURCE";
  const safeLink = (href, label, detail, className = "") => href
    ? `<a class="${className}" href="${memoEscape(href)}"><b>${memoEscape(label)}</b><small>${memoEscape(detail)}</small></a>`
    : `<span class="${className} unavailable" aria-disabled="true"><b>${memoEscape(label)}</b><small>${memoEscape(detail)}</small></span>`;
  let continueChoice = safeLink("", "Continue from this version", "Available after a human review decision.", "continue");
  let retryChoice = safeLink("", "Try again from parent", "Available after a reviewer rejects this version and records notes.", "retry");
  let rejectChoice = safeLink("", "Reject version", "A rejected revision remains immutable history.", "reject");

  if (status === "review" || status === "stale") {
    // The review drawer is the only surface that may record a human decision.
    continueChoice = safeLink(reviewHref, "Continue from this version", "Open human review to choose how this version continues.", "continue");
    retryChoice = safeLink(reviewHref, "Try again from parent", `Open human review to reject it with notes, then branch from ${parentLabel}.`, "retry");
    rejectChoice = safeLink(reviewHref, "Reject version", "Open human review to reject it; the revision will remain visible as history.", "reject");
  } else if (hasRequestedChanges) {
    continueChoice = safeLink(continueHref, "Continue from this version", "Open a new proposal that keeps this rejected version’s review notes and intent.", "continue");
    retryChoice = safeLink(retryParentHref, "Try again from parent", `Open a sibling proposal from ${parentLabel}, carrying the reviewer’s notes.`, "retry");
    rejectChoice = safeLink("", "Reject version", "Already rejected; this version is retained as immutable history only.", "reject");
  } else if (status === "approved") {
    continueChoice = safeLink(continueHref, "Continue from this version", "Open a normal new proposal from the approved baseline.", "continue");
    retryChoice = safeLink("", "Try again from parent", "This approved version has no rejected branch to retry.", "retry");
    rejectChoice = safeLink("", "Reject version", "Approved versions are not removed here; create a new reviewed successor instead.", "reject");
  } else if (status === "rejected") {
    const reason = decision === "reject"
      ? "Rejected without a change request; it remains history only."
      : "Rejected versions can branch only when a reviewer records requested changes.";
    continueChoice = safeLink("", "Continue from this version", reason, "continue");
    retryChoice = safeLink("", "Try again from parent", reason, "retry");
    rejectChoice = safeLink("", "Reject version", "Already rejected; this version is retained as immutable history only.", "reject");
  }
  return `<section class="structure-review-choices" aria-label="Choices for this revision">
    <header><span>What should happen next?</span><small>Versions are never overwritten or deleted.</small></header>
    <div>
      ${continueChoice}
      ${retryChoice}
      ${rejectChoice}
    </div>
  </section>`;
}

/** Keep the Three.js scene and its immutable revision/progress records together. */
export function buildStructurePreviewContext(payload = {}, structureId, { revisionId = "", source = false } = {}) {
  const id = String(structureId || "");
  if (!id) return null;
  const revisions = Array.isArray(payload?.revisions) ? payload.revisions : [];
  const current = payload?.revision || payload?.currentRevision || revisions[0] || null;
  let selectedRevision = source ? null : revisionId
    ? revisions.find((revision) => revision.id === revisionId) || (current?.id === revisionId ? current : null)
    : current;
  if (!source && revisionId && !selectedRevision) return null;

  const versions = revisions.map((revision) => ({
    revision,
    structure: resolveStructureLineageFromBody(revision?.body || {}, id),
  })).filter((entry) => entry.structure);
  let structure = selectedRevision
    ? resolveStructureLineageFromBody(selectedRevision.body || {}, id)
    : resolveStructureFromBody({}, id, { allowCatalogFallback: true });
  if (!structure && !revisionId) {
    selectedRevision = null;
    structure = resolveStructureFromBody({}, id, { allowCatalogFallback: true });
  }
  if (!structure) return null;

  const tickets = (Array.isArray(payload?.tickets) ? payload.tickets : [])
    .filter((ticket) => ticketTargetsStructure(ticket, id));
  const decisions = (Array.isArray(payload?.reviewDecisions) ? payload.reviewDecisions : [])
    .filter((decision) => !selectedRevision || decision.revisionId === selectedRevision.id);
  const sourceTicket = selectedRevision?.sourceTicketId
    ? tickets.find((ticket) => ticket.id === selectedRevision.sourceTicketId) || null
    : tickets[0] || null;

  return {
    structure: { ...structure, catalogSource: !selectedRevision && Boolean(structure.catalogSource) },
    lineageStructureId: id,
    artifact: payload?.artifact || null,
    revision: selectedRevision,
    versions,
    tickets,
    sourceTicket,
    latestDecision: decisions[0] || null,
  };
}

export function structureProposalInstruction(instruction, structure = {}, { mode = "fork" } = {}) {
  const requested = String(instruction || "").trim();
  const id = String(structure.id || "city_cordon_ring_segmented");
  const name = String(structure.name || structure.label || "City Cordon Segment A");
  if (!requested) return "";
  const policy = mode === "exact" ? "upsert_city_cordon_ring" : "fork_city_cordon_variant";
  return [
    `Target Structure source: ${name} (${id}).`,
    `Structure change policy: ${policy}.`,
    "This is a human proposal for a project-owned Structure Atlas revision.",
    "Never overwrite the catalog source or reuse its reserved identity for changed geometry.",
    "If the request keeps the exact catalog specification, add that exact source. If it changes dimensions, openings, tunnel shell, or road layout, fork a new custom radial successor with a new stable identity.",
    "A request for two or three roads means parallel road lanes inside the end-to-end opening; record lane width, shoulders, median, and remaining clearance as reviewable design intent.",
    `Requested change: ${requested}`,
  ].join(" ");
}

export function structureExplorationBrief(requestedChange, structure = {}, { projectDescription = "", mode = "fork", baseRevisionId = "", branch = null } = {}) {
  const requested = text(requestedChange);
  const identity = `${structure?.name || "This structure"} (${structure?.id || "unassigned identity"})`;
  const changePolicy = mode === "exact"
    ? "Use the exact repository source unless the human explicitly chooses a changed successor."
    : "Explore a project-owned successor; never overwrite the catalog source or reuse its reserved identity for changed geometry.";
  const exploration = [
    `Structure change chat: ${structure?.name || "structure"}`,
    `Target: ${identity}`,
    `Pinned base revision: ${baseRevisionId || "repository source"}`,
    branch?.kind ? `Revision path: ${branch.kind} (${branch.reviewedRevisionId})` : "Revision path: continue from the selected version",
    `Requested change: ${requested}`,
    changePolicy,
    "Compare consequences for geometry, assembly, gameplay, production, and compatibility.",
    "Do not stage a revision, launch a paid side quest, or start DCC/Unreal work until the human explicitly chooses the next action.",
  ].join("\n");
  const projectContext = text(projectDescription) ? `Project context:\n${text(projectDescription)}` : "";
  return [exploration, projectContext].filter(Boolean).join("\n\n");
}

export function isStructureChangeConversation(source = "") {
  return /^Structure change chat:/m.test(String(source || "")) && /^Requested change:/m.test(String(source || ""));
}

export function initStructures({ api, toast, esc, fileUrl, getProjectId, navigate, onNavigate = null, openRadialPreview, onRouteStateChange = null, routeHref = null }) {
  const root = document.getElementById("panel");
  let response = null;
  let imageLibrary = [];
  let selectedBuildingId = "";
  let selectedRevisionId = "";
  let active = false;
  let loading = false;
  let queueing = false;
  let generatingNote = "";
  let pollTimer = null;
  let workspaceMode = localStorage.getItem("wl_buildings_workspace") === "session" ? "session" : "atlas";
  let mobilePane = "conversation";
  let conversations = [];
  let conversationDetail = null;
  let selectedConversationId = "";
  let sessionLoaded = false;
  let sessionLoading = false;
  let sessionError = "";
  let sessionDraft = "";
  let sessionStartBrief = "";
  let startingSession = false;
  let sendingMessage = false;
  let waitingForReply = false;
  let waitingAfterSequence = 0;
  let questActionId = "";
  let startingProductionId = "";
  let proposalOpen = false;
  let proposalTargetId = "";
  let proposalDraft = "";
  let proposalBaseRevisionId = "";
  let proposalRetryRevisionId = "";
  const keptOptionIds = new Set();

  function getRouteState() {
    return {
      workspace: workspaceMode,
      revisionId: selectedRevisionId,
      structureId: selectedBuildingId,
      conversationId: selectedConversationId,
      conversationName: text(conversationDetail?.conversation?.title || conversationDetail?.conversation?.name),
      pane: workspaceMode === "session" && proposalTargetId && !conversationDetail?.conversation ? "chat" : mobilePane,
      successorRevisionId: proposalBaseRevisionId,
      retryRevisionId: proposalRetryRevisionId,
    };
  }

  function notifyRouteStateChange() {
    onRouteStateChange?.(getRouteState());
  }

  function href(descriptor) {
    return routeHref?.(descriptor) || "#";
  }

  function setWorkspaceMode(value) {
    const next = value === "session" ? "session" : "atlas";
    if (workspaceMode === next) return false;
    workspaceMode = next;
    if (next === "atlas") proposalOpen = false;
    localStorage.setItem("wl_buildings_workspace", workspaceMode);
    notifyRouteStateChange();
    return true;
  }

  function setProposalOpen(value, targetId = selectedBuildingId || "city_cordon_ring_segmented") {
    const next = Boolean(value);
    const nextTarget = next ? String(targetId || "city_cordon_ring_segmented") : "";
    if (proposalOpen === next && proposalTargetId === nextTarget) return false;
    proposalOpen = next;
    proposalTargetId = nextTarget;
    notifyRouteStateChange();
    return true;
  }

  function setProposalBranch({ successorRevisionId = "", retryRevisionId = "" } = {}) {
    const nextRetry = String(retryRevisionId || "");
    const nextSuccessor = nextRetry ? "" : String(successorRevisionId || "");
    if (proposalBaseRevisionId === nextSuccessor && proposalRetryRevisionId === nextRetry) return false;
    proposalBaseRevisionId = nextSuccessor;
    proposalRetryRevisionId = nextRetry;
    notifyRouteStateChange();
    return true;
  }

  function clearProposalBranch() {
    return setProposalBranch();
  }

  function setSelectedRevisionId(value) {
    const next = String(value || "");
    if (selectedRevisionId === next) return false;
    selectedRevisionId = next;
    notifyRouteStateChange();
    return true;
  }

  function setSelectedBuildingId(value) {
    const next = String(value || "");
    if (selectedBuildingId === next) return false;
    selectedBuildingId = next;
    notifyRouteStateChange();
    return true;
  }

  function setMobilePane(value) {
    const next = ["conversation", "context", "quests"].includes(value) ? value : "conversation";
    if (mobilePane === next) return false;
    mobilePane = next;
    notifyRouteStateChange();
    return true;
  }

  function currentRevision() {
    const revisions = Array.isArray(response?.revisions) ? response.revisions : [];
    return revisions.find((revision) => revision.id === selectedRevisionId) || response?.revision || revisions[0] || null;
  }

  function pendingReviewRevision() {
    const revision = currentRevision();
    return revision && ["review", "stale"].includes(String(revision.status || "").toLowerCase()) ? revision : null;
  }

  function proposalActionLabel(fallback = "Open change chat") {
    const revision = pendingReviewRevision();
    return revision ? `Review R${String(revisionNumber(revision)).padStart(2, "0")} before changing` : fallback;
  }

  function currentBody() {
    return currentRevision()?.body || {};
  }

  function currentBuildings() {
    const planned = planStructures(currentBody());
    if (planned.some((entry) => entry.id === "city_cordon_ring_segmented")) return planned;
    const sourceSegment = planStructures({ radialStructures: [CITY_CORDON_PREVIEW_SPECIFICATION] })[0];
    return sourceSegment ? [{ ...sourceSegment, catalogSource: true }, ...planned] : planned;
  }

  function activeTicket() {
    return (response?.tickets || []).find((ticket) => ACTIVE_TICKET_STATUSES.has(ticket.status));
  }

  function productionTicketFor(buildingId, revisionId = currentRevision()?.id) {
    return (response?.productionTickets || []).find((ticket) =>
      ticket.spec?.workId === buildingId &&
      (!revisionId || ticket.spec?.buildingPlanRevisionId === revisionId));
  }

  function imageFor(buildingId, kind) {
    const note = `building:${buildingId}:${kind}`;
    return imageLibrary.find((image) => image.note === note) || null;
  }

  function promptFor(building, kind) {
    const candidate = building.visualPrompts?.[kind];
    return text(candidate?.prompt || candidate);
  }

  function renderLoading() {
    root.innerHTML = `<main class="buildings-page buildings-loading" aria-busy="true">
      <div class="buildings-loading-rule"></div><div class="buildings-loading-title"></div>
      <div class="buildings-loading-grid"><i></i><i></i><i></i></div>
      <p>Opening the structure atlas…</p>
    </main>`;
  }

  function renderWorkspaceSwitch() {
    const running = runningSideQuests().length;
    return `<nav class="building-workspace-switch" aria-label="Structures workspace">
      <a href="${esc(href({ route: "structures", state: { tab: "atlas", revision: selectedRevisionId, pane: mobilePane } }))}" data-buildings-workspace="atlas" class="${workspaceMode === "atlas" ? "on" : ""}" aria-current="${workspaceMode === "atlas" ? "page" : "false"}"><span>Structure plan</span><small>Large-scale assemblies, routes, and production readiness</small></a>
      <a href="${esc(href({ route: "structures", state: { tab: "session", revision: selectedRevisionId, pane: mobilePane } }))}" data-buildings-workspace="session" class="${workspaceMode === "session" ? "on" : ""}" aria-current="${workspaceMode === "session" ? "page" : "false"}"><span>Explore design</span><small>Conversation + optional studies${running ? ` · ${running} running` : ""}</small></a>
    </nav>`;
  }

  function structureLifecycleStage(revision = currentRevision(), ticket = activeTicket()) {
    const status = String(revision?.status || "").toLowerCase();
    if (status === "approved") return 4;
    if (ticket) return 2;
    if (status === "review") return 3;
    if (["rejected", "stale", "superseded"].includes(status)) return 1;
    if (revision) return 2;
    if (proposalOpen) return 1;
    return 0;
  }

  function latestChangeRequest() {
    return (Array.isArray(response?.reviewDecisions) ? response.reviewDecisions : [])
      .find((decision) => decision.decision === "change_requested" && text(decision.feedback));
  }

  function reviewDecisionFor(revisionId) {
    const id = String(revisionId || "");
    if (!id) return null;
    return (Array.isArray(response?.reviewDecisions) ? response.reviewDecisions : [])
      .filter((decision) => String(decision.revisionId || "") === id)
      .sort((left, right) => String(right.createdAt || "").localeCompare(String(left.createdAt || "")))[0] || null;
  }

  function proposalBranchRevisionId() {
    return proposalRetryRevisionId || proposalBaseRevisionId;
  }

  function proposalChangeRequest() {
    const decisions = Array.isArray(response?.reviewDecisions) ? response.reviewDecisions : [];
    const branchRevisionId = proposalBranchRevisionId();
    return decisions.find((decision) => decision.revisionId === branchRevisionId && decision.decision === "change_requested") || null;
  }

  function proposalBranchCopy() {
    const branchRevisionId = proposalBranchRevisionId();
    const decision = proposalChangeRequest();
    if (!branchRevisionId || !decision) return { notice: "", draft: "" };
    const revision = (response?.revisions || []).find((item) => item.id === branchRevisionId);
    const label = `R${String(revisionNumber(revision)).padStart(2, "0")}`;
    const notes = text(decision.feedback);
    const recordedRequest = String(revision?.changeNote || "").split("Requested change:").slice(1).join("Requested change:").trim();
    if (proposalRetryRevisionId) {
      return {
        notice: `<section class="structure-proposal-branch"><b>Try again from this version’s parent</b><p>${esc(`R${String(revisionNumber(revision)).padStart(2, "0")} was rejected with notes. This new proposal starts from its parent, not from the rejected geometry.`)}</p><blockquote>${esc(notes)}</blockquote></section>`,
        draft: `Start again from the parent of ${label}; do not inherit the rejected geometry.\n\nOriginal request to try again:\n${recordedRequest || "Restate the intended replacement here."}\n\nReviewer notes to correct:\n${notes}`,
      };
    }
    return {
      notice: `<section class="structure-proposal-branch"><b>Continue the rejected version</b><p>${esc(`${label} stays immutable history. This new proposal keeps its intent while addressing the reviewer’s notes.`)}</p><blockquote>${esc(notes)}</blockquote></section>`,
      draft: `Continue ${label} and address these reviewer notes:\n${notes}\n\nRequested change:\n`,
    };
  }

  function proposalBranch() {
    return proposalRetryRevisionId
      ? { kind: "retry_parent", reviewedRevisionId: proposalRetryRevisionId }
      : proposalBaseRevisionId
        ? { kind: "continue_rejected_revision", reviewedRevisionId: proposalBaseRevisionId }
        : null;
  }

  function structureChatTarget() {
    const selected = currentBuildings().find((item) => item.id === (proposalTargetId || selectedBuildingId));
    if (selected) return selected;
    const match = sourceBrief().match(/^Target:\s*.+\(([^()]+)\)\s*$/m);
    return currentBuildings().find((item) => item.id === match?.[1])
      || planStructures({ radialStructures: [CITY_CORDON_PREVIEW_SPECIFICATION] })[0];
  }

  function beginStructureChat(structureId, { draft = "", successorRevisionId = "", retryRevisionId = "" } = {}) {
    setSelectedBuildingId(structureId || selectedBuildingId || "city_cordon_ring_segmented");
    proposalTargetId = structureId || selectedBuildingId || "city_cordon_ring_segmented";
    proposalDraft = draft;
    setProposalBranch({ successorRevisionId, retryRevisionId });
    selectedConversationId = "";
    conversationDetail = null;
    sessionLoaded = true;
    sessionError = "";
    sessionDraft = draft;
    setMobilePane("conversation");
    setWorkspaceMode("session");
    renderWorkingSession();
    requestAnimationFrame(() => root.querySelector("#structure-chat-draft")?.focus());
  }

  function renderStructureLifecycle({ revision = currentRevision(), ticket = activeTicket(), catalogSource = false, structureId = selectedBuildingId || "city_cordon_ring_segmented" } = {}) {
    const current = structureLifecycleStage(revision, ticket);
    const selectedStructure = currentBuildings().find((entry) => entry.id === structureId);
    const lineageStructureId = selectedStructure?.sourceStructureId || structureId;
    const stages = [
      ["Specification", catalogSource ? "Catalog source" : "Project source"],
      ["Change chat", "Human + agent"],
      ["Agent work", "Board + run log"],
      ["Human review", "Approve or request changes"],
      ["Approved", "Project baseline"],
    ];
    const artifact = response?.artifact;
    const approvedRevisionId = artifact?.approvedRevisionId || "";
    const metadata = revision ? `<dl class="structure-revision-identity">
      <div><dt>Selected</dt><dd>R${esc(String(revisionNumber(revision)).padStart(2, "0"))} · ${esc(pretty(revision.status))}</dd></div>
      <div><dt>Author</dt><dd>${esc(revision.createdBy || "Unknown")}</dd></div>
      <div><dt>Revision ID</dt><dd><code>${esc(String(revision.id || "").slice(0, 12))}</code></dd></div>
      <div><dt>Content hash</dt><dd><code>${esc(revision.contentSha256 ? String(revision.contentSha256).slice(0, 12) : "Pending submission")}</code></dd></div>
      <div><dt>Approved baseline</dt><dd>${approvedRevisionId ? (approvedRevisionId === revision.id ? "This revision" : `Revision ${esc(String(approvedRevisionId).slice(0, 8))}`) : "None yet"}</dd></div>
    </dl>` : `<p class="structure-template-truth"><b>Repository template</b> · not in this project · not approved</p>`;
    const revisionTree = renderStructureRevisionTree(response?.revisions || [], {
      structureId: lineageStructureId,
      selectedRevisionId: revision?.id || "",
      hrefFor: ({ source, revisionId }) => source
        ? href({ route: "preview", structureId: lineageStructureId, state: { version: "source" } })
        : href({ route: "structures", state: { revision: revisionId, tab: workspaceMode, pane: mobilePane } }),
    });
    const reviewHref = revision ? href({ route: "revisionReview", revisionId: revision.id, revisionName: `Structure revision R${String(revisionNumber(revision)).padStart(2, "0")}` }) : "";
    const continueHref = revision ? href({ route: "structureChange", structureId, state: {
      revision: revision.id,
      ...(String(revision.status || "").toLowerCase() === "rejected" ? { successor: revision.id } : {}),
    } }) : "";
    const retryParentHref = revision ? href({ route: "structureChange", structureId, state: { revision: revision.id, retry: revision.id } }) : "";
    const reviewDecision = reviewDecisionFor(revision?.id);
    return `<section class="structure-change-flow" aria-label="Structure revision lifecycle">
      <header><div><p class="buildings-eyebrow">Specification to approval</p><h2>Every change stays visible.</h2></div><p>The catalog source is never overwritten. Agents can propose and prepare candidates; a separate human decides whether a revision becomes approved.</p></header>
      ${metadata}
      ${revisionTree}
      <ol>${stages.map(([label, detail], index) => `<li class="${index < current ? "done" : index === current ? "current" : ""}"><span>${String(index + 1).padStart(2, "0")}</span><div><b>${label}</b><small>${detail}</small></div></li>`).join("")}</ol>
      <div class="structure-flow-actions"><a href="${esc(href({ route: "board" }))}">See agent work on Board</a><a href="${esc(href({ route: "reviews" }))}">Open human decisions in Needs You</a>${latestChangeRequest() ? `<button type="button" data-successor-from-feedback="${esc(String(latestChangeRequest().revisionId || ""))}">Create successor from latest feedback</button>` : ""}</div>
      ${latestChangeRequest() ? `<details class="structure-review-history"><summary>Latest requested changes · R${esc(String(latestChangeRequest().revisionNumber || "—"))}</summary><p>${esc(text(latestChangeRequest().feedback))}</p><small>${esc(latestChangeRequest().decidedBy || "Human reviewer")}</small></details>` : ""}
      ${renderStructureReviewChoices({ revision, reviewDecision, continueHref, retryParentHref, reviewHref })}
    </section>`;
  }

  function renderNoProject() {
    root.innerHTML = `<main class="buildings-page buildings-empty">
      <div class="buildings-empty-mark" aria-hidden="true"><span></span><span></span><span></span></div>
      <p class="buildings-eyebrow">Structure atlas</p><h1>Select a project<br><em>to begin the dossier.</em></h1>
      <p>Buildings, walls, bridges, cordons, monuments, and other large world assemblies stay isolated inside one project. Small props and materials remain in Assets.</p>
    </main>`;
  }

  function renderEmptyPlan() {
    const ticket = activeTicket();
    const source = { ...planStructures({ radialStructures: [CITY_CORDON_PREVIEW_SPECIFICATION] })[0], catalogSource: true };
    root.innerHTML = `<main class="buildings-page buildings-empty buildings-brief-empty">
      ${renderWorkspaceSwitch()}
      <header class="structure-inventory-head"><p class="buildings-eyebrow">Structures / source modules</p><h1><strong>1</strong> repository template.</h1><p>This reusable source piece is available to inspect, but it is not in this project and is not approved. Propose a project version to begin the tracked revision flow.</p></header>
      <article class="structure-source-card">
        <span class="structure-source-number">01</span>
        <span class="structure-source-copy"><small>CATALOG SOURCE · NOT YET PROJECT-OWNED</small><b>City Cordon Segment A</b><span>One reusable trapezoidal wall length. The preview contains exactly this piece.</span></span>
        <span class="structure-source-dimensions"><b>402 × 15 × 18 m</b><small>CENTRELINE × DEPTH × HEIGHT</small></span>
        <span class="structure-source-actions"><a href="${esc(href({ route: "preview", structureId: source.id, structureName: source.name }))}" class="structure-source-action" data-open-canonical-cordon>Open in 3D <i aria-hidden="true">→</i></a><button type="button" data-propose-structure="${esc(source.id)}">${esc(proposalActionLabel())}</button></span>
      </article>
      ${renderStructureLifecycle({ revision: null, ticket, catalogSource: true, structureId: source.id })}
      ${renderPostApprovalExportStep({ structure: source, assetReady: false })}
      ${ticket ? renderQueueState(ticket) : ""}
    </main>`;
    bind();
  }

  function renderQueueState(ticket) {
    const status = pretty(ticket.status);
    return `<section class="building-queue-state" role="status" aria-live="polite">
      <span class="building-queue-pulse" aria-hidden="true"></span>
      <div><b>${esc(status)}</b><p>${esc(ticket.title || "The structure atlas is being drafted")}</p></div>
      <small>Worldloom is preserving the brief as a revision input. <a href="${esc(href({ route: "ticket", ticketId: ticket.id, ticketName: ticket.title || ticket.type }))}">Open agent stages and run log →</a></small>
    </section>`;
  }

  function renderRevisionRail(revision) {
    const revisions = Array.isArray(response?.revisions) ? response.revisions : [];
    const rows = revisions.length ? revisions : revision ? [revision] : [];
    return `<nav class="building-revision-rail" aria-label="Structure atlas revisions">
      ${rows.slice(0, 8).map((item) => {
        const status = String(item.status || "candidate").toLowerCase();
        const awaitsDecision = ["review", "stale"].includes(status);
        const label = `R${String(revisionNumber(item)).padStart(2, "0")}`;
        const radial = Array.isArray(item.body?.radialStructures) ? item.body.radialStructures[0] : null;
        const descriptor = awaitsDecision
          ? { route: "revisionReview", revisionId: item.id, revisionName: `${radial?.label || "Structure revision"} ${label}` }
          : { route: "structures", state: { revision: item.id, tab: workspaceMode, pane: mobilePane } };
        return `<a href="${esc(href(descriptor))}" ${awaitsDecision ? "" : `data-building-revision="${esc(item.id)}"`} class="${item.id === revision?.id ? "on" : ""}" aria-current="${item.id === revision?.id ? "page" : "false"}">
          <span>${label}</span><b>${esc(awaitsDecision ? "Review now" : pretty(status))}</b>
        </a>`;
      }).join("")}
    </nav>`;
  }

  function renderBuildingCard(building, index) {
    const selected = building.id === selectedBuildingId;
    const radial = building.atlasEntryKind === "radial_structure";
    const macro = building.atlasEntryKind === "macro_structure";
    const rooms = roomSequence(building.interior).length;
    return `<a href="${esc(href({ route: "structure", structureId: building.id, structureName: building.name, state: { revision: selectedRevisionId, tab: workspaceMode, pane: mobilePane } }))}" class="building-atlas-card ${selected ? "selected" : ""}" data-building-id="${esc(building.id)}" aria-current="${selected ? "page" : "false"}">
      <span class="building-card-number">${String(index + 1).padStart(2, "0")}</span>
      <span class="building-card-copy"><small>${building.isCanonicalCordonSegment ? "Source module · one twelfth" : radial ? "Radial infrastructure" : macro ? pretty(building.kind || "Large structure") : "Whole building"}</small><b>${esc(building.name)}</b><span>${esc(building.summary || "Large-scale production assembly")}</span></span>
      <span class="building-card-meta">${building.isCanonicalCordonSegment ? "1 reusable segment · 402 × 15 × 18 m" : radial ? `1 reusable segment · optional ${Number(building.segment?.count || 0)}-placement recipe` : macro ? pretty(building.assembly?.method || "Production intent") : rooms ? `${rooms} interior beats` : "Interior dossier"} <i aria-hidden="true">→</i></span>
    </a>`;
  }

  function renderPlan() {
    const revision = currentRevision();
    const body = currentBody();
    const buildings = currentBuildings();
    if (!buildings.some((building) => building.id === selectedBuildingId)) setSelectedBuildingId(buildings[0]?.id || "");
    const selected = buildings.find((building) => building.id === selectedBuildingId);
    const ticket = activeTicket();
    root.innerHTML = `<main class="buildings-page building-atlas-shell">
      ${renderWorkspaceSwitch()}
      <header class="building-atlas-head">
        <div><p class="buildings-eyebrow">Structures / assembly dossier</p><h1>${esc(text(body.title || body.name) || "Structure Atlas")}</h1><p>${esc(text(body.direction?.thesis || body.summary || body.thesis || body.sourceBrief || body.brief) || "Large-scale world structures, assembly logic, and production coverage.")}</p></div>
        <div class="building-atlas-stamp"><span>Current survey</span><b>R${String(revisionNumber(revision)).padStart(2, "0")}</b><small>${esc(pretty(revision?.status || "candidate"))}</small></div>
      </header>
      ${renderRevisionRail(revision)}
      ${ticket ? renderQueueState(ticket) : ""}
      <div class="building-atlas-layout">
        <aside class="building-atlas-index" aria-label="Large structures">
          <header><span>${buildings.length}</span><div><b>Atlas entries</b><small>Large structures + infrastructure intent</small></div></header>
          <div>${buildings.length ? buildings.map(renderBuildingCard).join("") : `<p class="building-missing">This revision contains no large structures yet.</p>`}</div>
        </aside>
        <section class="building-dossier" aria-live="polite">${selected ? renderDossier(selected, buildings.indexOf(selected)) : renderMissingDossier()}</section>
      </div>
      ${selected?.atlasEntryKind === "radial_structure" ? renderStructureLifecycle({ revision, ticket, catalogSource: Boolean(selected.catalogSource), structureId: selected.id }) : `<section class="building-iteration building-chat-entry">
        <div><p class="buildings-eyebrow">Change through conversation</p><h2>Tell the Structure agent what should change.</h2><p>Your message, clarifications, agent work, and proposed version stay together in one chat. Nothing becomes a version until you explicitly prepare it for review.</p></div>
        <button class="primary building-primary" type="button" data-propose-structure="${esc(selected?.id || "")}">Open change chat</button>
      </section>`}
    </main>`;
    bind();
  }

  function renderMissingDossier() {
    return `<div class="building-missing-dossier"><p class="buildings-eyebrow">No dossier selected</p><h2>Add a large structure in the next revision.</h2></div>`;
  }

  function renderDossier(building, index) {
    if (building.atlasEntryKind === "radial_structure") return renderRadialStructureDossier(building, index);
    if (building.atlasEntryKind === "macro_structure") return renderMacroStructureDossier(building, index);
    return `<article>
      <header class="building-dossier-head"><span>${String(index + 1).padStart(2, "0")} / FULL BUILDING</span><div><h2>${esc(building.name)}</h2><p>${esc(building.summary || "Whole-building architectural family")}</p></div><b>${esc(text(building.type || building.category || building.role) || "Building family")}</b></header>
      <section class="building-history" aria-labelledby="history-title"><div class="building-section-title"><span>01</span><div><small>Palimpsest</small><h3 id="history-title">Three histories, one silhouette</h3></div></div>
        <div class="building-history-grid">${historyLayer("Original function", building.original, "01")}${historyLayer("Arena conversion", building.conversion, "02")}${historyLayer("Ongoing collapse", building.collapse, "03")}</div>
      </section>
      <section class="building-visual-section"><div class="building-section-title"><span>02</span><div><small>Elevation + section</small><h3>Outside and inside must agree</h3></div></div>
        <div class="building-visual-grid">${renderVisual(building, "exterior", "Full-building exterior", "Exterior elevation, material history, entrances, and landmark silhouette.")}${renderVisual(building, "cutaway", "Interior cutaway", "Room sequence, circulation, lighting, and combat readability in one section drawing.")}</div>
      </section>
      <section class="building-paired-brief"><div>${sectionHeading("03", "Envelope", "Exterior + gameplay")}${renderFacts(building.exterior)}${renderFacts(building.gameplay)}</div>
        <div class="building-interior-dossier">${sectionHeading("04", "Interior", "A beautiful route through the building")}${renderInterior(building.interior)}</div>
      </section>
      <section class="building-production-plan">${sectionHeading("05", "Production routing", "See every route before authoring starts")}${renderProduction(building)}${renderProductionGate(building)}</section>
    </article>`;
  }

  function renderMacroStructureDossier(structure, index) {
    const dimensions = structure.dimensionsM || {};
    const siting = structure.siting || {};
    const assembly = structure.assembly || {};
    const gameplay = structure.gameplay || {};
    const production = structure.production || {};
    const instances = siting.instanceCount || {};
    return `<article>
      <header class="building-dossier-head"><span>${String(index + 1).padStart(2, "0")} / LARGE STRUCTURE</span><div><h2>${esc(structure.name)}</h2><p>${esc(structure.summary)}</p></div><b>${esc(pretty(structure.kind || "Structure"))}</b></header>
      <section>${sectionHeading("01", "Scale + siting", "A macro-scale world element, not a small asset")}
        <dl class="building-facts">
          <div><dt>Dimensions</dt><dd>${esc(`${dimensions.x ?? "—"} × ${dimensions.y ?? "—"} × ${dimensions.z ?? "—"} m`)}</dd></div>
          <div><dt>Instances</dt><dd>${esc(`${instances.min ?? "—"}–${instances.max ?? "—"}`)}</dd></div>
          <div><dt>Preferred zones</dt><dd>${esc(list(siting.preferredZones).map(text).filter(Boolean).join(" · ") || "—")}</dd></div>
          <div><dt>Placement intent</dt><dd>${esc(text(siting.placementIntent) || "—")}</dd></div>
        </dl>
      </section>
      <section>${sectionHeading("02", "Assembly", "Reusable construction and exact connection intent")}
        <dl class="building-facts">
          <div><dt>Method</dt><dd>${esc(pretty(assembly.method || "unspecified"))}</dd></div>
          <div><dt>Reusable modules</dt><dd>${esc(list(assembly.reusableModuleIds).map(text).filter(Boolean).join(" · ") || "None declared")}</dd></div>
          <div><dt>Connections</dt><dd>${esc(text(assembly.connectionStrategy) || "—")}</dd></div>
          <div><dt>Surfaces</dt><dd>${esc(text(assembly.surfaceStrategy) || "—")}</dd></div>
        </dl>
      </section>
      <section>${sectionHeading("03", "Gameplay + production", "Type-specific intent without building-only room requirements")}
        <dl class="building-facts">
          <div><dt>Gameplay role</dt><dd>${esc(text(gameplay.role) || "—")}</dd></div>
          <div><dt>Traversal</dt><dd>${esc(list(gameplay.traversal).map(text).filter(Boolean).join(" · ") || "None")}</dd></div>
          <div><dt>Authoring route</dt><dd>${esc(pretty(production.authoringRoute || "unspecified"))}</dd></div>
          <div><dt>Collision</dt><dd>${esc(pretty(production.collisionIntent || "unspecified"))}</dd></div>
          <div><dt>Review evidence</dt><dd>${esc(list(production.reviewDeliverables).map(pretty).join(" · ") || "—")}</dd></div>
        </dl>
        <div class="building-production-gate"><header><div><span>DESIGN INTENT ONLY</span><h4>Production requires a type-specific approved handoff.</h4><p>This entry cannot enter the small-asset generator or inherit building-only interior checks. Asset authoring and approval happen first; export and Unreal integration remain downstream gates.</p></div><b class="blocked">Planning only</b></header></div>
      </section>
    </article>${renderPostApprovalExportStep({ structure, assetReady: false })}`;
  }

  function renderPostApprovalExportStep({ structure, assetReady = false, glbKey = "" } = {}) {
    const unreal = structure?.unreal || {};
    const targetPath = unreal.staticMeshPath || structure?.ue?.soft_path || "Not assigned";
    return `<section class="structure-export-step ${assetReady ? "ready" : "locked"}" aria-label="Post-approval export">
      <header>
        <div><p class="buildings-eyebrow">After the asset is finished</p><h2>Export</h2><p>${assetReady
          ? "This approved, versioned asset can now leave authoring as a pinned source package. Importing it into Unreal is a separate integration job."
          : "Export is not part of specification or authoring. It unlocks only after a versioned asset has finished validation and human approval."}</p></div>
        <b>${assetReady ? "Ready to export" : "Locked"}</b>
      </header>
      <ol>
        <li class="${assetReady ? "done" : "current"}"><span>01</span><div><b>Finish asset</b><small>${assetReady ? "Approved immutable revision" : "Author, validate, and approve first"}</small></div></li>
        <li class="${assetReady ? "current" : ""}"><span>02</span><div><b>Export source package</b><small>Geometry, materials, collision, units, and target intent</small></div></li>
        <li><span>03</span><div><b>Integrate in Unreal</b><small>Separate workspace, worker, validation, and human gate</small></div></li>
      </ol>
      <div class="structure-export-actions">
        ${assetReady && glbKey ? `<a class="primary building-primary" href="${esc(fileUrl(glbKey))}" download>Export approved source</a>` : `<button type="button" disabled>Finish asset before export</button>`}
        <small>${assetReady ? "This exports source only; it does not change an Unreal project." : "An approved specification alone is not a finished asset."}</small>
      </div>
      <details><summary>Future Unreal destination</summary><dl class="building-facts"><div><dt>Intended content path</dt><dd>${esc(targetPath)}</dd></div><div><dt>Integration status</dt><dd>Not connected in this interface</dd></div></dl></details>
    </section>`;
  }

  function renderRadialStructureDossier(structure, index) {
    if (structure.isCanonicalCordonSegment) return renderCordonSegmentDossier(structure, index);
    const segment = structure.segment || {};
    const assembly = structure.assembly || {};
    const unreal = structure.unreal || {};
    const acceptance = structure.acceptance || {};
    const vertices = Object.entries(segment.localTopVerticesM || {});
    const labels = Array.from({ length: Number(segment.count || 0) }, (_, itemIndex) => `${assembly.actorLabelPrefix || "Instance_"}${String(itemIndex).padStart(2, "0")}`);
    return `<article>
      <header class="building-dossier-head"><span>${String(index + 1).padStart(2, "0")} / REUSABLE SOURCE MODULE</span><div><h2>${esc(structure.name)}</h2><p>${esc(structure.summary)}</p></div><b>Design specification</b></header>
      <section>${sectionHeading("01", "Exact geometry", "One connection-safe reusable segment")}
        <dl class="building-facts">
          <div><dt>Source piece</dt><dd>1 reusable segment</dd></div>
          <div><dt>Centreline</dt><dd>${esc(`${segment.centrelineLengthM} m`)}</dd></div>
          <div><dt>Inner / outer</dt><dd>${esc(`${segment.innerFaceLengthM} / ${segment.outerFaceLengthM} m`)}</dd></div>
          <div><dt>Depth / height</dt><dd>${esc(`${segment.radialDepthM} / ${segment.totalHeightM} m`)}</dd></div>
          <div><dt>End mitre</dt><dd>${esc(`${segment.endMitreDeg}° per end`)}</dd></div>
          <div><dt>Vertical span</dt><dd>${esc(`${segment.baseZM} m to ${segment.topZM} m`)}</dd></div>
        </dl>
        <details><summary>Local top vertices</summary><ol>${vertices.map(([name, vertex]) => `<li><b>${esc(pretty(name))}</b><small>${esc(`X ${vertex.x} m · Y ${vertex.y} m`)}</small></li>`).join("")}</ol></details>
      </section>
      <section>${sectionHeading("02", "Optional future assembly", `${segment.count} placements from the same source piece`)}
        <dl class="building-facts">
          <div><dt>Placement radius</dt><dd>${esc(`${assembly.placementRadiusCm} cm`)}</dd></div>
          <div><dt>Midpoint radius</dt><dd>${esc(`${assembly.midpointRadiusM} m`)}</dd></div>
          <div><dt>Opposite flats</dt><dd>${esc(`${assembly.oppositeFlatDiameterM} m`)}</dd></div>
          <div><dt>Yaw</dt><dd>${esc(`Angle ${Number(assembly.yawOffsetDeg) < 0 ? "−" : "+"} ${Math.abs(Number(assembly.yawOffsetDeg || 0))}°`)}</dd></div>
          <div><dt>Folder</dt><dd>${esc(assembly.outlinerFolder || "—")}</dd></div>
          <div><dt>Tag</dt><dd>${esc(assembly.actorTag || "—")}</dd></div>
        </dl>
        <details><summary>Actor labels</summary><p>${esc(labels.join(" · "))}</p></details>
      </section>
      <section class="building-radial-preview-cta">${sectionHeading("03", "Interactive planning proxy", "Inspect this one source segment in Three.js")}
        <div class="building-radial-preview-action"><div><b>One source mesh · one preview piece</b><p>Orbit, zoom, frame, inspect the two end openings and connection planes, and save a screenshot. The optional repeated assembly is deliberately not shown.</p><small>Design intent only — no DCC or Unreal asset is created by opening the preview.</small></div><div class="building-radial-preview-buttons"><a href="${esc(href({ route: "preview", structureId: structure.sourceStructureId || structure.id, structureName: structure.name, state: { revision: selectedRevisionId } }))}" class="primary building-primary building-route-link" data-open-radial-preview="${esc(structure.id)}">Open one segment in 3D</a><button type="button" data-propose-structure="${esc(structure.id)}">${esc(proposalActionLabel())}</button></div></div>
      </section>
      <section>${sectionHeading("04", "Source requirements", "Requirements for a finished reusable asset")}
        <dl class="building-facts">
          <div><dt>Material slots</dt><dd>${esc(Object.entries(unreal.materialSlots || {}).sort((left, right) => Number(left[1]) - Number(right[1])).map(([name]) => name).join(" · "))}</dd></div>
          <div><dt>Collision</dt><dd>${esc(pretty(unreal.collision || "unspecified"))}</dd></div>
          <div><dt>Mesh reuse</dt><dd>${unreal.reuseSingleMesh ? "One mesh, all instances" : "Invalid"}</dd></div>
          <div><dt>Seam tolerance</dt><dd>${esc(`${acceptance.seamToleranceCm} cm`)}</dd></div>
        </dl>
        <div class="building-production-gate"><header><div><span>DESIGN INTENT ONLY</span><h4>Finish and approve the source asset before export.</h4><p>This revision records geometry and assembly intent. It does not create an engine package or start Unreal integration.</p></div><b class="blocked">Planning only</b></header></div>
      </section>
    </article>${renderPostApprovalExportStep({ structure, assetReady: false })}`;
  }

  function renderCordonSegmentDossier(structure, index) {
    const segment = structure.segment || {};
    const assembly = structure.assembly || {};
    const unreal = structure.unreal || {};
    const acceptance = structure.acceptance || {};
    const vertices = Object.entries(segment.localTopVerticesM || {});
    return `<article class="cordon-segment-dossier">
      <header class="building-dossier-head"><span>${String(index + 1).padStart(2, "0")} / SOURCE MODULE</span><div><h2>${esc(structure.name)}</h2><p>${esc(structure.summary)}</p></div><b>${structure.catalogSource ? "Available specification" : "Design specification"}</b></header>
      <section>${sectionHeading("01", "The structure you place", "Exactly one reusable wall segment")}
        <div class="cordon-one-piece-callout"><strong>1</strong><div><b>source mesh</b><span>One twelfth of the optional ring. No ring is created or previewed here.</span></div></div>
        <dl class="building-facts">
          <div><dt>Centreline length</dt><dd>${esc(`${segment.centrelineLengthM} m`)}</dd></div>
          <div><dt>Radial depth</dt><dd>${esc(`${segment.radialDepthM} m`)}</dd></div>
          <div><dt>Total height</dt><dd>${esc(`${segment.totalHeightM} m`)}</dd></div>
          <div><dt>Inner / outer faces</dt><dd>${esc(`${segment.innerFaceLengthM} / ${segment.outerFaceLengthM} m`)}</dd></div>
          <div><dt>End mitre</dt><dd>${esc(`${segment.endMitreDeg}° per end`)}</dd></div>
          <div><dt>Pivot / scale</dt><dd>Centreline centre · 1.0</dd></div>
        </dl>
        <details><summary>Exact local top vertices</summary><ol>${vertices.map(([name, vertex]) => `<li><b>${esc(pretty(name))}</b><small>${esc(`X ${vertex.x} m · Y ${vertex.y} m`)}</small></li>`).join("")}</ol></details>
      </section>
      <section class="building-radial-preview-cta">${sectionHeading("02", "Three.js source preview", "Inspect only this one placeable length")}
        <div class="building-radial-preview-action"><div><b>1 source mesh · 1 previewed piece</b><p>Orbit, zoom, frame, inspect its trapezoidal connection planes, and save a screenshot. The other eleven pieces are deliberately absent.</p><small>Opening the preview is read-only. It does not place the segment or start Unreal production.</small></div><div class="building-radial-preview-buttons"><a href="${esc(href({ route: "preview", structureId: structure.id, structureName: structure.name, state: { revision: selectedRevisionId } }))}" class="primary building-primary building-route-link" data-open-radial-preview="${esc(structure.id)}">Open one segment in 3D</a><button type="button" data-propose-structure="${esc(structure.id)}">${esc(proposalActionLabel("Open change chat"))}</button></div></div>
      </section>
      <section>${sectionHeading("03", "Source requirements", "What the finished reusable asset must preserve")}
        <dl class="building-facts">
          <div><dt>Materials</dt><dd>CordonStructure · CordonFloor</dd></div>
          <div><dt>Collision</dt><dd>${esc(pretty(unreal.collision || "unspecified"))}</dd></div>
          <div><dt>Connection planes</dt><dd>${unreal.preserveConnectionPlanes ? "Must remain exact" : "Not declared"}</dd></div>
          <div><dt>Unit scale</dt><dd>1 m = 100 cm · scale 1.0</dd></div>
          <div><dt>Seam tolerance</dt><dd>${esc(`${acceptance.seamToleranceCm} cm in an approved assembly`)}</dd></div>
        </dl>
        <div class="building-production-gate"><header><div><span>PLACEMENT IS A SEPARATE REVISION</span><h4>Choose where and how many after the source module is approved.</h4><p>A placement needs a stable placement ID, exact asset revision, transform, owning section, validation evidence, and human approval. This preview makes none of those decisions for you.</p></div><b class="blocked">Not placed</b></header></div>
        <details class="cordon-future-assembly"><summary>Optional future ring recipe · 12 placements</summary><p>If you later approve the full cordon, place this same source mesh 12 times at ${esc(String(assembly.placementRadiusCm))} cm with 30° spacing. That assembly recipe is not the structure shown in the preview.</p></details>
      </section>
    </article>${renderPostApprovalExportStep({ structure, assetReady: false })}`;
  }

  function historyLayer(label, value, number) {
    return `<article><span>${number}</span><h4>${label}</h4><p>${esc(value || "Not specified in this revision.")}</p></article>`;
  }

  function sectionHeading(number, kicker, title) {
    return `<div class="building-section-title"><span>${number}</span><div><small>${esc(kicker)}</small><h3>${esc(title)}</h3></div></div>`;
  }

  function renderVisual(building, kind, label, description) {
    const image = imageFor(building.id, kind);
    const prompt = promptFor(building, kind);
    const note = `building:${building.id}:${kind}`;
    return `<figure class="building-visual-card ${image ? "has-image" : ""}">
      <div class="building-visual-frame">${image ? `<img src="${esc(fileUrl(image.key))}" alt="${esc(`${building.name} ${label.toLowerCase()} concept`)}" loading="lazy">` : `<div class="building-drawing-placeholder" aria-hidden="true"><span></span><i></i><b>${kind === "cutaway" ? "SECTION A—A" : "ELEVATION 01"}</b></div>`}
        ${generatingNote === note ? `<div class="building-image-progress" role="status">Drawing concept…</div>` : ""}
      </div>
      <figcaption><div><small>${esc(label)}</small><p>${esc(description)}</p></div><button type="button" data-generate-building-image="${esc(kind)}" data-building-id="${esc(building.id)}" ${!prompt || generatingNote ? "disabled" : ""}>${image ? "Generate another reference" : "Generate reference image"}</button></figcaption>
      ${prompt ? `<details><summary>Concept prompt</summary><p>${esc(prompt)}</p></details>` : `<p class="building-prompt-missing">Add a ${esc(kind)} visual prompt in the next atlas revision to generate this view.</p>`}
    </figure>`;
  }

  function renderFacts(value) {
    if (!value || (typeof value === "object" && !Object.keys(value).length)) return `<p class="building-missing">Not specified in this revision.</p>`;
    if (typeof value === "string") return `<p class="building-prose">${esc(value)}</p>`;
    if (Array.isArray(value)) return `<ul class="building-fact-list">${value.map((item) => `<li>${esc(text(item))}</li>`).join("")}</ul>`;
    return `<dl class="building-facts">${Object.entries(value).filter(([key]) => !/prompt|image/i.test(key)).map(([key, detail]) => `<div><dt>${esc(pretty(key))}</dt><dd>${esc(text(detail) || "—")}</dd></div>`).join("")}</dl>`;
  }

  function roomSequence(interior = {}) {
    return list(interior.roomSequence || interior.rooms || interior.zones || interior.spatialSequence);
  }

  function renderInterior(interior = {}) {
    const rooms = roomSequence(interior);
    const fields = [
      ["Circulation", interior.circulationRoutes || interior.circulation || interior.routes || interior.movement],
      ["Lighting", interior.lightingStrategy || interior.lighting || interior.light],
      ["Readability", interior.readabilityRules || interior.readability || interior.landmarks || interior.wayfinding],
      ["Beauty moments", interior.beautyMoments],
    ].filter(([, value]) => text(value));
    const intent = text(interior.designIntent || interior.summary);
    return `${intent ? `<p class="building-prose">${esc(intent)}</p>` : ""}
      <ol class="building-room-sequence">${rooms.length ? rooms.map((room, index) => {
        const roomDetail = text(room.detail || room.description || room.purpose);
        const atmosphere = [
          room.artDirection ? `Art: ${text(room.artDirection)}` : "",
          room.lighting ? `Light: ${text(room.lighting)}` : "",
          room.readabilityCue ? `Cue: ${text(room.readabilityCue)}` : "",
        ].filter(Boolean).join(" · ");
        return `<li><span>${String(index + 1).padStart(2, "0")}</span><div><b>${esc(text(room.name || room.label || room) || `Room ${index + 1}`)}</b>${roomDetail ? `<p>${esc(roomDetail)}</p>` : ""}${atmosphere ? `<small>${esc(atmosphere)}</small>` : ""}</div></li>`;
      }).join("") : `<li class="empty"><span>—</span><div><b>Room sequence not specified</b><p>Request circulation and room landmarks in the next revision.</p></div></li>`}</ol>
      <dl class="building-interior-facts">${fields.map(([label, value]) => `<div><dt>${label}</dt><dd>${esc(text(value))}</dd></div>`).join("")}</dl>`;
  }

  function productionValue(production, keys) {
    for (const key of keys) if (production?.[key] != null) return production[key];
    return [];
  }

  function renderAssemblyPlan(plan = {}) {
    const runs = list(plan.moduleRuns);
    if (!runs.length) return `<p class="building-assembly-missing">Assembly module runs have not been planned yet.</p>`;
    const moduleCount = new Set(runs.map((run) => run.moduleId).filter(Boolean)).size;
    const instanceCount = runs.reduce((total, run) => total + [run.count?.x, run.count?.y, run.count?.z].reduce((product, count) => product * Math.max(1, Number(count || 1)), 1), 0);
    const grid = plan.gridM || {};
    const gridLabel = [grid.x, grid.y, grid.z].every((value) => Number.isFinite(Number(value)))
      ? `${Number(grid.x)} × ${Number(grid.y)} × ${Number(grid.z)} m`
      : "—";
    return `<div class="building-assembly-summary">
      <dl>
        <div><dt>Instances</dt><dd>${instanceCount}</dd></div>
        <div><dt>Module types</dt><dd>${moduleCount}</dd></div>
        <div><dt>Snap grid</dt><dd>${esc(gridLabel)}</dd></div>
        <div><dt>Max piece</dt><dd>${esc(plan.maxModuleExtentM ? `${plan.maxModuleExtentM} m` : "—")}</dd></div>
        <div><dt>Surfaces</dt><dd>${esc(pretty(plan.surfaceStrategy || "unspecified"))}</dd></div>
        <div><dt>Texel target</dt><dd>${esc(plan.targetTexelDensityPxPerM ? `${plan.targetTexelDensityPxPerM} px/m` : "—")}</dd></div>
      </dl>
      <details><summary>Module runs</summary><ol>${runs.map((run) => {
        const origin = run.originM || {};
        const count = run.count || {};
        return `<li><b>${esc(pretty(run.moduleId || run.id || "module"))}</b><small>${esc(`${run.id || "run"} · ${Number(origin.x || 0)}, ${Number(origin.y || 0)}, ${Number(origin.z || 0)} m · ${Math.max(1, Number(count.x || 1))}×${Math.max(1, Number(count.y || 1))}×${Math.max(1, Number(count.z || 1))} · ${Number(run.rotationQuarterTurns || 0) * 90}°`)}</small></li>`;
      }).join("")}</ol></details>
    </div>`;
  }

  function renderProduction(building) {
    const production = building.production || {};
    const full = productionValue(production, ["fullBuilding", "wholeBuilding", "prefab", "assembly"]);
    const fullMeta = full && typeof full === "object"
      ? [pretty(full.assemblyMethod), full.includesPlayableInterior ? "Playable interior" : "", text(full.reviewDeliverables)].filter(Boolean).join(" · ")
      : "";
    const groups = [
      ["Structural modules", productionValue(production, ["kitModules", "structuralModules", "exteriorModules", "kits"])],
      ["Interior modules", productionValue(production, ["interiorModules", "roomModules", "interiors"])],
      ["Materials", productionValue(production, ["materials", "surfaceSets"])],
      ["Props + dressing", productionValue(production, ["props", "setDressing", "details"])],
    ];
    return `<div class="building-production-flow"><article class="building-full-first"><span>01 / ASSEMBLED DELIVERABLE</span><h4>${esc(text(full?.label || full?.name || full) || building.name)}</h4><p>${esc(text(full?.description || full?.detail) || "One assembled, reviewable full building with exterior and interior visible together.")}</p>${fullMeta ? `<small>${esc(fullMeta)}</small>` : ""}${renderAssemblyPlan(full?.assemblyPlan)}<b>ASSEMBLED BUILDING</b></article>
      <div class="building-production-branch" aria-label="Supporting production assets">${groups.map(([label, values], index) => `<article><span>0${index + 2}</span><h4>${label}</h4>${renderProductionItems(values)}</article>`).join("")}</div></div>`;
  }

  function renderProductionItems(values) {
    const items = list(values);
    return items.length ? `<ul>${items.map((item) => `<li>${esc(text(item.name || item.label || item))}${text(item.detail || item.description || item.purpose) ? `<small>${esc(text(item.detail || item.description || item.purpose))}</small>` : ""}</li>`).join("")}</ul>` : `<p>Defined as the atlas is refined.</p>`;
  }

  const routeLabel = (route) => ({
    catalog_reuse: "Reuse approved catalog asset",
    image_to_3d_prop: "Image-to-3D · portable prop only",
    blender_asset: "Blender asset",
    blender_structural_kit: "Blender modular kit",
    blender_material: "Blender UV + material pass",
    blender_building: "Blender building",
  })[route] || pretty(route);

  function renderProductionGate(building) {
    const revision = currentRevision();
    const isApproved = Boolean(revision?.id && revision.status === "approved" && response?.artifact?.approvedRevisionId === revision.id);
    const ticket = productionTicketFor(building.id, revision?.id);
    const liveCoverage = response?.productionCoverage?.[building.id] || [];
    const frozenRoutes = ticket?.spec?.routingSnapshot;
    const productionPlan = ticket?.spec?.buildingPlan?.productionAssets;
    const needLookup = new Map([
      ...(productionPlan?.fullBuilding ? [[productionPlan.fullBuilding.id, { label: productionPlan.fullBuilding.label, quantity: 1 }]] : []),
      ...["kitModules", "interiorModules", "materials", "props"].flatMap((key) =>
        list(productionPlan?.[key]).map((need) => [need.id, { label: need.label, quantity: need.quantity }])),
    ]);
    const coverage = Array.isArray(frozenRoutes) && frozenRoutes.length
      ? frozenRoutes.map((row) => {
          const need = needLookup.get(row.needId) || {};
          const catalogEvidence = row.route === "catalog_reuse"
            ? `Frozen catalog asset ${row.catalogAssetUid} · v${row.catalogVersion} · Sources: ${list(row.sourceArtifactKeys).map(text).join(", ")}`
            : "Frozen when this Blender job was launched.";
          return {
            id: row.needId,
            label: need.label || pretty(row.needId),
            group: row.group,
            quantity: need.quantity || 1,
            proposedRoute: row.route,
            reason: catalogEvidence,
          };
        })
      : liveCoverage;
    const usesFrozenRoutes = Array.isArray(frozenRoutes) && frozenRoutes.length > 0;
    const status = String(ticket?.status || "");
    const busy = ["blocked", "ready", "leased", "in_progress"].includes(status);
    const awaitingReview = ["submitted", "review"].includes(status);
    const complete = status === "approved";
    const failed = ["failed", "rejected", "escalated", "stale"].includes(status);
    const phaseReport = ticket?.result?.phaseReport || {};
    const counts = coverage.reduce((summary, row) => {
      summary[row.proposedRoute] = (summary[row.proposedRoute] || 0) + 1;
      return summary;
    }, {});
    const phases = [
      ["Plan", isApproved ? "complete" : "current", isApproved ? "Approved" : "Approval required"],
      ["Routes", isApproved ? (ticket ? "complete" : "current") : "locked", `${coverage.length} decisions`],
      ["Geometry", phaseReport.geometry === "complete" ? "complete" : busy || failed ? "current" : "locked", phaseReport.geometry === "complete" ? "Validated" : busy || failed ? pretty(status) : "After routing"],
      ["Textures", phaseReport.materials === "complete" ? "complete" : "locked", phaseReport.materials === "complete" ? "Manifest validated" : "After geometry"],
      ["Assembly", phaseReport.assembly === "complete" ? "complete" : "locked", phaseReport.assembly === "complete" ? "Review evidence ready" : "After textures"],
      ["Asset approval", complete ? "complete" : "locked", complete ? "Versioned catalog asset" : "After assembly review"],
    ];
    const action = !isApproved
      ? `<button type="button" class="primary building-primary" disabled>Approve plan before production</button><small>Open Needs you to approve this exact Atlas revision.</small>`
      : !ticket
        ? `<button type="button" class="primary building-primary" data-start-building-production="${esc(building.id)}" ${startingProductionId ? "disabled" : ""}>${startingProductionId === building.id ? "Starting Blender build…" : "Confirm routes and start Blender build"}</button><small>Starts 1 bounded Blender job with a $20 cap. Geometry is audited before UVs/textures, then approved catalog assets are assembled. Portable prop gaps stay separate for the later dressing pass; architecture never uses image-to-3D.</small>`
        : busy
          ? `<button type="button" disabled>Blender build ${esc(pretty(status))}</button><small>Progress, artifacts, attempts, and cost are visible on the Board.</small>`
          : awaitingReview
            ? `<button type="button" class="primary building-primary" data-open-production-review>Review Blender build</button><small>Review exterior, cutaway, interior, exports, and the audit before placement.</small>`
            : complete
              ? `<button type="button" class="primary building-primary" data-open-building-assets>View promoted building asset</button><small>The validated build is now a finished, versioned catalog asset. Export is the next separate step.</small>`
              : `<button type="button" class="primary building-primary" data-start-building-production="${esc(building.id)}">${failed ? "Retry from approved plan" : "Start Blender build"}</button><small>${failed ? `Previous build is ${esc(pretty(status))}; the approved plan remains unchanged.` : "The build has not started."}</small>`;
    const siting = building.siting || {};
    const zones = list(siting.preferredZones).map(text).filter(Boolean);
    const instances = siting.instanceCount || {};

    return `<section class="building-production-gate" aria-label="Production readiness">
      <header><div><span>EXECUTION PLAN</span><h4>Catalog first. Blender for architecture.</h4><p>${usesFrozenRoutes ? "These are the immutable routes and exact catalog versions received by the Blender controller." : "Worldloom has classified each production need before any authoring job is launched."}</p></div><b class="${isApproved ? "ready" : "blocked"}">${usesFrozenRoutes ? "Routes frozen" : isApproved ? "Plan approved" : "Planning only"}</b></header>
      <ol class="building-production-lifecycle">${phases.map(([label, state, detail]) => `<li class="${state}"><span></span><b>${label}</b><small>${detail}</small></li>`).join("")}</ol>
      <div class="building-route-summary">
        ${Object.entries(counts).map(([route, count]) => `<div><b>${count}</b><span>${esc(routeLabel(route))}</span></div>`).join("") || `<p>No production needs are defined yet.</p>`}
      </div>
      <details class="building-routing-table" ${!ticket ? "open" : ""}><summary>Review ${usesFrozenRoutes ? "frozen " : ""}${coverage.length} routing decision${coverage.length === 1 ? "" : "s"}</summary>
        <div role="table" aria-label="Production routes">${coverage.map((row) => `<div role="row"><span role="cell"><b>${esc(row.label)}</b><small>${esc(`${pretty(row.group)} · qty ${row.quantity}`)}</small></span><span role="cell"><b>${esc(routeLabel(row.proposedRoute))}</b><small>${esc(row.reason)}</small></span></div>`).join("")}</div>
      </details>
      ${ticket?.result?.audit ? renderBuildingReview(ticket) : ""}
      <div class="building-production-action">${action}</div>
      ${renderPostApprovalExportStep({ structure: building, assetReady: complete, glbKey: ticket?.result?.artifactKeys?.glb?.[0] || "" })}
    </section>`;
  }

  function renderBuildingReview(ticket) {
    const result = ticket.result || {};
    const renderKeys = result.renderKeys || {};
    const views = ["exterior", "cutaway", "interior", "reference_comparison"];
    const checks = result.audit?.checks || {};
    const glbKey = result.artifactKeys?.glb?.[0];
    return `<section class="building-evidence-review" aria-label="Validated Blender evidence">
      <header><div><span>VALIDATED EVIDENCE</span><b>${result.audit?.passed ? "Audit passed" : "Audit unavailable"}</b></div>${glbKey ? `<a href="${esc(fileUrl(glbKey))}" download>Download GLB</a>` : ""}</header>
      <div class="building-evidence-renders">${views.map((view) => renderKeys[view] ? `<figure><img src="${esc(fileUrl(renderKeys[view]))}" alt="${esc(`${pretty(view)} Blender review`)}" loading="lazy"><figcaption>${esc(pretty(view))}</figcaption></figure>` : "").join("")}</div>
      <ul>${Object.entries(checks).map(([check, passed]) => `<li class="${passed ? "pass" : "fail"}"><span>${passed ? "✓" : "×"}</span>${esc(pretty(check))}</li>`).join("")}</ul>
    </section>`;
  }

  function conversationBase(projectId = getProjectId()) {
    return `/projects/${encodeURIComponent(projectId)}/building-design-conversations`;
  }

  function messageSequence(message) {
    return Number(message?.sequence ?? message?.sequenceNumber ?? message?.number ?? 0);
  }

  function latestMessageSequence() {
    return Math.max(0, ...(conversationDetail?.messages || []).map(messageSequence));
  }

  function conversationId() {
    return selectedConversationId || conversationDetail?.conversation?.id || "";
  }

  function sideQuests() {
    return Array.isArray(conversationDetail?.sideQuests) ? conversationDetail.sideQuests : [];
  }

  function questState(quest) {
    const disposition = String(quest?.disposition || "open").toLowerCase();
    const ticketStatus = String(quest?.ticketStatus || quest?.ticket?.status || "ready").toLowerCase();
    if (disposition === "accepted") return "accepted";
    if (disposition === "dismissed") return "dismissed";
    if (["submitted", "review"].includes(ticketStatus)) return "ready";
    if (["failed", "canceled", "cancelled"].includes(ticketStatus)) return "failed";
    if (["leased", "in_progress"].includes(ticketStatus)) return "running";
    return "queued";
  }

  function runningSideQuests() {
    return sideQuests().filter((quest) => ["queued", "running"].includes(questState(quest)));
  }

  function currentReplyTicket(tickets = conversationDetail?.tickets || []) {
    return [...tickets]
      .filter((ticket) => ticket.spec?.mode === "building_conversation_reply_v1")
      .sort((a, b) => String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || "")))[0] || null;
  }

  function activeReplyTicket(tickets = conversationDetail?.tickets || []) {
    const ticket = currentReplyTicket(tickets);
    return Boolean(ticket && ["ready", "leased", "in_progress"].includes(ticket.status));
  }

  function suggestionList(message) {
    const source = message?.suggestedSideQuests ?? message?.payload?.suggestedSideQuests ?? message?.suggestions ?? message?.suggestion ?? message?.sideQuestSuggestions ?? message?.sideQuestSuggestion ?? message?.metadata?.suggestions ?? [];
    return list(source).filter(Boolean);
  }

  function questOptions(quest) {
    const source = quest?.proposal?.options ?? quest?.options ?? quest?.result?.options ?? quest?.output?.options ?? quest?.findings ?? [];
    return Array.isArray(source) ? source : Object.entries(source || {}).map(([id, value]) => ({ id, ...(value || {}) }));
  }

  function atlasBaseRevisionId() {
    const atlas = conversationDetail?.atlas || {};
    return atlas.revision?.id || atlas.currentRevision?.id || atlas.artifact?.currentRevisionId || currentRevision()?.id || "";
  }

  function sourceBrief() {
    const conversation = conversationDetail?.conversation || {};
    const firstMessage = (conversationDetail?.messages || []).find((message) => message.kind === "source_brief" || message.sequence === 1);
    return text(conversation.sourceBrief || conversation.brief || conversation.context?.sourceBrief || firstMessage?.content || conversationDetail?.atlas?.plan?.sourceBrief || conversationDetail?.atlas?.buildingPlan?.sourceBrief || currentBody().sourceBrief || response?.project?.description);
  }

  function preferenceRows(kind) {
    const conversation = conversationDetail?.conversation || {};
    const ledger = conversation.preferenceLedger || conversation.preferences || {};
    const memory = conversation.memory || {};
    const direct = kind === "more" ? (memory.moreOf || ledger.moreOf || ledger.moreLike || ledger.prefer || ledger.likes) : (memory.lessOf || ledger.lessOf || ledger.lessLike || ledger.avoid || ledger.dislikes);
    if (direct) return list(direct).map(text).filter(Boolean);
    return list(ledger).filter((item) => {
      const sentiment = String(item?.sentiment || item?.kind || item?.type || "").toLowerCase();
      return kind === "more" ? /more|like|prefer|keep/.test(sentiment) : /less|avoid|dislike|remove/.test(sentiment);
    }).map(text).filter(Boolean);
  }

  function renderSessionStart() {
    return `<section class="building-session-start">
      <p class="buildings-eyebrow">Working Session / source folio</p>
      <h1>Start with the full brief.<br><em>Keep it intact.</em></h1>
      <p>Open a conversational design desk around the original world description. Nothing here generates an image, asset, or Atlas revision until you explicitly choose it.</p>
      <form id="building-session-start">
        <label for="building-session-brief">Immutable source brief</label>
        <textarea id="building-session-brief" name="sourceBrief" maxlength="50000" rows="16" required placeholder="Paste the complete world style description, architectural needs, interior goals, production constraints, and references…">${esc(sessionStartBrief)}</textarea>
        <div><span><b>50,000</b> character capacity</span><button class="primary building-primary" type="submit" ${startingSession ? "disabled" : ""}>${startingSession ? "Opening desk…" : "Open working session"}</button></div>
      </form>
    </section>`;
  }

  function requestedChangeFromBrief(value = sourceBrief()) {
    const match = String(value || "").match(/^Requested change:\s*([^\n]*(?:\n(?![A-Z][^\n]+:).*)*)/m);
    return text(match?.[1]);
  }

  function renderStructureChatStart() {
    const structure = structureChatTarget();
    const revision = currentRevision();
    const branch = proposalBranchCopy();
    const draft = sessionDraft || proposalDraft || branch.draft;
    const baseLabel = revision ? `R${String(revisionNumber(revision)).padStart(2, "0")} · ${pretty(revision.status)}` : "repository source";
    return `<main class="buildings-page structure-chat-shell">
      <a class="structure-chat-back" href="${esc(href({ route: "structure", structureId: structure?.id || selectedBuildingId, structureName: structure?.name, state: { revision: selectedRevisionId } }))}">← Back to ${esc(structure?.name || "structure")}</a>
      <header class="structure-chat-head"><div><p class="buildings-eyebrow">Structure change chat</p><h1>What should change?</h1><p>Talk it through with the Structure agent. It can ask questions, show its work, or say that the current structure contract cannot represent the request. A version appears only after structured agent work succeeds.</p></div><dl><div><dt>Structure</dt><dd>${esc(structure?.name || "Selected structure")}</dd></div><div><dt>Starting from</dt><dd>${esc(baseLabel)}</dd></div></dl></header>
      ${branch.notice}
      <section class="structure-chat-thread" aria-label="New structure change conversation">
        <article class="structure-chat-welcome"><span>STRUCTURE AGENT</span><h2>Describe the outcome in your own words.</h2><p>I’ll keep the selected version pinned, ask about anything ambiguous, and prepare a reviewable version only when the geometry can be represented and validated.</p></article>
        <form id="structure-chat-start" class="structure-chat-composer">
          <label for="structure-chat-draft">Message the Structure agent</label>
          <textarea id="structure-chat-draft" name="content" rows="5" maxlength="20000" placeholder="For example: keep the exact connection planes, but add two road lanes through the longitudinal opening…" required>${esc(draft)}</textarea>
          <div><small>This message starts the chat; it does not create or approve a version.</small><button class="primary building-primary" type="submit" ${startingSession ? "disabled" : ""}>${startingSession ? "Sending…" : "Send"}</button></div>
        </form>
      </section>
    </main>`;
  }

  function renderPreferenceLedger() {
    const more = preferenceRows("more");
    const less = preferenceRows("less");
    const rows = (items, empty) => items.length ? `<ol>${items.map((item) => `<li>${esc(item)}</li>`).join("")}</ol>` : `<p>${empty}</p>`;
    return `<aside class="building-context-folio building-session-pane ${mobilePane === "context" ? "show" : ""}" aria-label="Source context">
      <header><span>FOLIO A</span><h2>Source + preferences</h2></header>
      <section class="building-source-brief"><h3>Immutable source brief</h3><p>${esc(sourceBrief() || "No source brief was returned for this session.")}</p></section>
      <section class="building-preference-ledger"><h3>Preference ledger</h3><div><b>More like</b>${rows(more, "No positive direction recorded yet.")}</div><div><b>Less like</b>${rows(less, "No avoidances recorded yet.")}</div></section>
    </aside>`;
  }

  function renderSuggestion(message, suggestion, index) {
    const id = suggestion.id || suggestion.suggestionId || `${message.id || messageSequence(message)}-${index}`;
    const spend = suggestion.estimatedSpend ?? suggestion.spend ?? suggestion.cost;
    const cap = suggestion.spendCap ?? suggestion.cap ?? suggestion.maxSpend;
    return `<section class="building-sidequest-suggestion">
      <span>OPTIONAL SIDE QUEST</span><h4>${esc(text(suggestion.title || suggestion.name) || "Focused design exploration")}</h4>
      <p>${esc(text(suggestion.summary || suggestion.brief || suggestion.description || suggestion.rationale || suggestion.scope))}</p>
      <div><small>${spend != null ? `Estimated spend ${esc(spend)}` : "Agent work is shown before it creates a version"}${cap != null ? ` · cap ${esc(cap)}` : ""}</small><button type="button" data-run-sidequest="${esc(id)}" data-message-id="${esc(message.id || "")}" data-suggestion="${encodeURIComponent(JSON.stringify(suggestion))}">${isStructureChangeConversation(sourceBrief()) ? "Prepare this change" : "Run side quest"}</button></div>
    </section>`;
  }

  function renderMessage(message, index) {
    const role = String(message.role || message.author || "assistant").toLowerCase();
    const assistant = !/user|human|designer/.test(role);
    const suggestions = suggestionList(message);
    const content = message.kind === "source_brief" && isStructureChangeConversation(message.content)
      ? requestedChangeFromBrief(message.content)
      : text(message.content || message.message || message.body || message.text);
    return `<article class="building-session-memo ${assistant ? "assistant" : "user"}">
      <header><span>${String(messageSequence(message) || index + 1).padStart(3, "0")}</span><b>${assistant ? "Studio memo" : "Designer note"}</b><time>${message.createdAt ? esc(new Date(message.createdAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })) : ""}</time></header>
      <div class="building-memo-copy">${formatStructureMemo(content || "—")}</div>
      ${suggestions.map((suggestion, suggestionIndex) => renderSuggestion(message, suggestion, suggestionIndex)).join("")}
    </article>`;
  }

  function renderReplyTicketStatus(ticket) {
    if (!ticket) return "";
    const status = String(ticket.status || "ready").toLowerCase();
    const activeReply = ["ready", "leased", "in_progress"].includes(status);
    const failedReply = ["failed", "escalated", "cancelled", "canceled"].includes(status);
    const label = activeReply ? `Sent to Structure agent · ${pretty(status)}` : failedReply ? "Structure agent reply failed" : "Structure agent reply delivered";
    const detail = activeReply
      ? "Your exact request is recorded above. The reply will appear here."
      : failedReply
        ? "Your request is still recorded above. Inspect the run or try the same request again."
        : "The memo above is the returned result. Continue here whenever you want another pass.";
    return `<div class="building-session-waiting ${failedReply ? "failed" : activeReply ? "active" : "complete"}" role="${failedReply ? "alert" : "status"}"><span></span><div><b>${esc(label)}</b><small>${esc(detail)}</small></div><div class="building-session-ticket-actions"><a href="${esc(href({ route: "ticket", ticketId: ticket.id, ticketName: ticket.title || "Structure agent discussion" }))}">${activeReply ? "See agent stages" : "Open agent run"} →</a>${failedReply ? `<button type="button" data-retry-structure-reply>Try again</button>` : ""}</div></div>`;
  }

  function consequence(option, key) {
    return text(option?.consequences?.[key] || option?.[`${key}Consequences`] || option?.[key]);
  }

  function renderQuestOption(quest, option, index) {
    const optionId = String(option.id || option.optionId || index + 1);
    const key = `${quest.id}:${optionId}`;
    const kept = keptOptionIds.has(key);
    return `<article class="building-quest-option ${kept ? "kept" : ""}">
      <header><span>${String(index + 1).padStart(2, "0")}</span><h4>${esc(text(option.title || option.name) || `Direction ${index + 1}`)}</h4></header>
      <p>${esc(text(option.summary || option.description || option.thesis))}</p>
      <dl><div><dt>Interior</dt><dd>${esc(consequence(option, "interior") || "No interior consequence recorded.")}</dd></div><div><dt>Gameplay</dt><dd>${esc(consequence(option, "gameplay") || "No gameplay consequence recorded.")}</dd></div><div><dt>Production</dt><dd>${esc(consequence(option, "production") || "No production consequence recorded.")}</dd></div></dl>
      <div class="building-option-actions">
        <button type="button" data-option-reaction="more" data-quest-id="${esc(quest.id)}" data-option-id="${esc(optionId)}">More like this</button>
        <button type="button" data-option-reaction="less" data-quest-id="${esc(quest.id)}" data-option-id="${esc(optionId)}">Less like this</button>
        <button type="button" data-keep-option="${esc(key)}">${kept ? "Kept as reference" : "Keep as reference"}</button>
        <button class="primary" type="button" data-accept-option="${esc(optionId)}" data-quest-id="${esc(quest.id)}" ${questActionId ? "disabled" : ""}>${isStructureChangeConversation(sourceBrief()) ? "Create reviewable version" : "Stage as Atlas revision"}</button>
      </div>
    </article>`;
  }

  function renderQuest(quest, index) {
    const status = questState(quest);
    const options = questOptions(quest);
    const proposal = quest.proposal || quest.result?.proposal || quest.result || {};
    const spend = quest.spent ?? quest.spend ?? quest.estimatedSpend ?? quest.cost;
    const cap = quest.spendCap ?? quest.cap ?? quest.maxSpend;
    return `<article class="building-sidequest-card status-${esc(status)}">
      <header><span>SQ-${String(index + 1).padStart(2, "0")}</span><b>${esc(pretty(status))}</b></header>
      <h3>${esc(text(quest.title || quest.suggestion?.title) || "Side quest")}</h3><p>${esc(text(proposal.summary || quest.summary || quest.brief || quest.suggestion?.summary || quest.suggestion?.brief || quest.scope))}</p>
      <div class="building-quest-budget"><span>${spend != null ? `Spend ${esc(spend)}` : "Spend pending"}</span><span>${cap != null ? `Cap ${esc(cap)}` : "No cap reported"}</span></div>
      ${status === "ready" ? `<div class="building-quest-comparisons">${options.length ? options.map((option, optionIndex) => renderQuestOption(quest, option, optionIndex)).join("") : `<p>Results are ready, but no comparison options were returned.</p>`}</div>` : ""}
      ${status === "failed" ? `<p class="building-quest-error">${esc(text(quest.error || quest.failureReason) || "This exploration failed. The conversation remains available.")}</p>` : ""}
      ${!["dismissed", "accepted"].includes(status) ? `<button class="building-dismiss-quest" type="button" data-dismiss-quest="${esc(quest.id)}" ${questActionId ? "disabled" : ""}>Dismiss quest</button>` : ""}
    </article>`;
  }

  function renderWorkingSession() {
    const conversation = conversationDetail?.conversation;
    if (sessionLoading && !conversation) {
      root.innerHTML = `<main class="buildings-page building-session-shell">${renderWorkspaceSwitch()}<section class="building-session-loading" aria-busy="true"><p>Opening the working ledger…</p></section></main>`;
      bind();
      return;
    }
    if (!conversation) {
      root.innerHTML = proposalTargetId
        ? `${sessionError ? `<p class="building-session-error">${esc(sessionError)}</p>` : ""}${renderStructureChatStart()}`
        : `<main class="buildings-page building-session-shell">${renderWorkspaceSwitch()}${sessionError ? `<p class="building-session-error">${esc(sessionError)}</p>` : ""}${renderSessionStart()}</main>`;
      bind();
      return;
    }
    const messages = Array.isArray(conversationDetail.messages) ? conversationDetail.messages : [];
    const quests = sideQuests();
    const running = runningSideQuests();
    const replyTicket = currentReplyTicket();
    if (isStructureChangeConversation(sourceBrief())) {
      const structure = structureChatTarget();
      const accepted = quests.find((quest) => questState(quest) === "accepted");
      const failed = quests.find((quest) => questState(quest) === "failed");
      const activeReply = activeReplyTicket();
      const reviewHref = accepted?.acceptedRevisionId ? href({ route: "revisionReview", revisionId: accepted.acceptedRevisionId, revisionName: `${structure?.name || "Structure"} proposed version` }) : "";
      root.innerHTML = `<main class="buildings-page structure-chat-shell">
        <a class="structure-chat-back" href="${esc(href({ route: "structure", structureId: structure?.id || selectedBuildingId, structureName: structure?.name, state: { revision: selectedRevisionId } }))}">← Back to ${esc(structure?.name || "structure")}</a>
        <header class="structure-chat-head"><div><p class="buildings-eyebrow">Structure change chat</p><h1>${esc(text(conversation.title) || `Change ${structure?.name || "structure"}`)}</h1><p>Your request, agent questions, work, and resulting version stay in this conversation.</p></div><span>CHAT ${esc(String(conversation.id || "").slice(-6).toUpperCase())}</span></header>
        <ol class="structure-chat-progress" aria-label="Change progress">
          <li class="done"><span>1</span><div><b>Requested</b><small>Message recorded</small></div></li>
          <li class="${activeReply || running.length ? "current" : messages.some((message) => String(message.role).toLowerCase() === "assistant") ? "done" : ""}"><span>2</span><div><b>Agent</b><small>${activeReply ? "Replying" : running.length ? "Preparing change" : failed ? "Work failed" : "Clarifying or ready"}</small></div></li>
          <li class="${accepted ? "done" : ""}"><span>3</span><div><b>Version</b><small>${accepted ? "Immutable candidate created" : "Not created"}</small></div></li>
          <li class="${accepted ? "current" : ""}"><span>4</span><div><b>Human review</b><small>${accepted ? "Waiting for your decision" : "After a valid version exists"}</small></div></li>
        </ol>
        ${accepted ? `<a class="structure-chat-review primary building-primary" href="${esc(reviewHref)}">Review proposed version →</a>` : ""}
        <section class="structure-chat-thread" aria-label="Structure change conversation">
          <div class="building-session-memos">${messages.map(renderMessage).join("")}${renderReplyTicketStatus(replyTicket)}${quests.map(renderQuest).join("")}</div>
          <form class="structure-chat-composer" id="building-session-message"><label for="building-session-draft">Reply</label><textarea id="building-session-draft" name="content" rows="4" maxlength="20000" placeholder="Answer the agent, correct its interpretation, or ask for another approach…">${esc(sessionDraft)}</textarea><div><small>Chat messages do not silently create or approve versions.</small><button class="primary building-primary" type="submit" ${sendingMessage || activeReply ? "disabled" : ""}>${sendingMessage ? "Sending…" : activeReply ? "Agent replying…" : "Send"}</button></div></form>
        </section>
      </main>`;
      bind();
      return;
    }
    root.innerHTML = `<main class="buildings-page building-session-shell">
      ${renderWorkspaceSwitch()}
      <header class="building-session-head"><div><p class="buildings-eyebrow">Structures / working session</p><h1>${esc(text(conversation.title || conversation.name) || "Structure working ledger")}</h1><p>Explore large world structures, assembly, gameplay, and production consequences without spending unless you explicitly launch a side quest.</p></div><span>SESSION ${esc(String(conversation.id || "").slice(-6).toUpperCase())}</span></header>
      <nav class="building-session-mobile-nav" aria-label="Working session sections"><button type="button" data-building-session-pane="conversation" class="${mobilePane === "conversation" ? "on" : ""}">Conversation</button><button type="button" data-building-session-pane="context" class="${mobilePane === "context" ? "on" : ""}">Context</button><button type="button" data-building-session-pane="quests" class="${mobilePane === "quests" ? "on" : ""}">Quests ${running.length ? `<b>${running.length}</b>` : ""}</button></nav>
      ${running.length ? `<div class="building-session-running-strip" role="status"><span></span><b>${running.length} side quest${running.length === 1 ? "" : "s"} running</b><small>The working conversation stays open.</small></div>` : ""}
      <div class="building-session-grid">
        ${renderPreferenceLedger()}
        <section class="building-session-ledger building-session-pane ${mobilePane === "conversation" ? "show" : ""}" aria-label="Design conversation">
          <header><span>LEDGER B</span><h2>Studio correspondence</h2><small>${messages.length} entries</small></header>
          <div class="building-session-memos">${messages.length ? messages.map(renderMessage).join("") : `<div class="building-session-first-note"><h3>The desk is open.</h3><p>Ask which large structures the world needs, how they should assemble, or what production assets make them buildable.</p></div>`}${renderReplyTicketStatus(replyTicket)}</div>
          <form class="building-session-composer" id="building-session-message"><label for="building-session-draft">Add to the working ledger</label><textarea id="building-session-draft" name="content" rows="4" maxlength="20000" placeholder="What large structures does this world need, and how should they assemble and play?">${esc(sessionDraft)}</textarea><div><small>Side quests never run automatically.</small><button class="primary building-primary" type="submit" ${sendingMessage ? "disabled" : ""}>${sendingMessage ? "Sending…" : "Send memo"}</button></div></form>
        </section>
        <aside class="building-sidequest-rail building-session-pane ${mobilePane === "quests" ? "show" : ""}" aria-label="Side quests"><header><span>RAIL C</span><h2>Side quests</h2><small>${quests.length} total</small></header>${quests.length ? quests.map(renderQuest).join("") : `<div class="building-no-quests"><h3>No spend in motion.</h3><p>When a studio memo suggests a useful focused exploration, you decide whether to run it.</p></div>`}</aside>
      </div>
    </main>`;
    bind();
  }

  function bind() {
    root.querySelectorAll("[data-buildings-workspace]").forEach((link) => {
      link.addEventListener("click", async (event) => {
        if (!isUnmodifiedPrimaryLinkClick(event)) return;
        event.preventDefault();
        setWorkspaceMode(link.dataset.buildingsWorkspace);
        if (workspaceMode === "session" && !sessionLoaded) await fetchConversationState();
        else render();
      });
    });
    root.querySelectorAll("[data-building-session-pane]").forEach((button) => {
      button.addEventListener("click", () => { setMobilePane(button.dataset.buildingSessionPane); renderWorkingSession(); });
    });
    root.querySelector("#building-session-start")?.addEventListener("submit", startConversation);
    root.querySelector("#structure-chat-start")?.addEventListener("submit", startStructureChat);
    root.querySelector("#building-session-brief")?.addEventListener("input", (event) => { sessionStartBrief = event.currentTarget.value; });
    root.querySelector("#building-session-message")?.addEventListener("submit", sendComposerMessage);
    root.querySelector("[data-retry-structure-reply]")?.addEventListener("click", () => {
      const latestHuman = [...(conversationDetail?.messages || [])].reverse().find((message) => /user|human|designer/.test(String(message.role || "").toLowerCase()));
      if (latestHuman?.content) postSessionMessage(latestHuman.content);
    });
    const draft = root.querySelector("#building-session-draft");
    draft?.addEventListener("input", () => { sessionDraft = draft.value; });
    root.querySelectorAll("[data-run-sidequest]").forEach((button) => {
      button.addEventListener("click", () => {
        let suggestion = {};
        try { suggestion = JSON.parse(decodeURIComponent(button.dataset.suggestion || "")); } catch { suggestion = {}; }
        launchSideQuest(suggestion, button.dataset.messageId);
      });
    });
    root.querySelectorAll("[data-dismiss-quest]").forEach((button) => button.addEventListener("click", () => dismissSideQuest(button.dataset.dismissQuest)));
    root.querySelectorAll("[data-accept-option]").forEach((button) => button.addEventListener("click", () => acceptSideQuestOption(button.dataset.questId, button.dataset.acceptOption)));
    root.querySelectorAll("[data-option-reaction]").forEach((button) => button.addEventListener("click", () => reactToOption(button.dataset.optionReaction, button.dataset.questId, button.dataset.optionId)));
    root.querySelectorAll("[data-keep-option]").forEach((button) => button.addEventListener("click", () => {
      const key = button.dataset.keepOption;
      if (keptOptionIds.has(key)) keptOptionIds.delete(key); else keptOptionIds.add(key);
      renderWorkingSession();
    }));
    root.querySelectorAll("[data-building-id]:not([data-generate-building-image])").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (!isUnmodifiedPrimaryLinkClick(event)) return;
        event.preventDefault();
        setSelectedBuildingId(link.dataset.buildingId);
        renderPlan();
      });
    });
    root.querySelectorAll("[data-building-revision]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (!isUnmodifiedPrimaryLinkClick(event)) return;
        event.preventDefault();
        setSelectedRevisionId(link.dataset.buildingRevision);
        setSelectedBuildingId("");
        renderPlan();
      });
    });
    root.querySelectorAll("[data-generate-building-image]").forEach((button) => {
      button.addEventListener("click", () => generateConcept(button.dataset.buildingId, button.dataset.generateBuildingImage));
    });
    root.querySelectorAll("[data-open-radial-preview]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (!isUnmodifiedPrimaryLinkClick(event)) return;
        event.preventDefault();
        const structure = currentBuildings().find((entry) => entry.id === link.dataset.openRadialPreview);
        if (structure) openRadialPreview?.(structure);
      });
    });
    root.querySelector("[data-open-canonical-cordon]")?.addEventListener("click", (event) => {
      if (!isUnmodifiedPrimaryLinkClick(event)) return;
      event.preventDefault();
      const structure = planStructures({ radialStructures: [CITY_CORDON_PREVIEW_SPECIFICATION] })[0];
      openRadialPreview?.(structure);
    });
    root.querySelectorAll("[data-propose-structure]").forEach((button) => {
      button.addEventListener("click", () => {
        const revision = pendingReviewRevision();
        if (revision) {
          onNavigate?.("revisionReview", {
            revisionId: revision.id,
            revisionName: `Structure revision R${String(revisionNumber(revision)).padStart(2, "0")}`,
          });
          return;
        }
        beginStructureChat(button.dataset.proposeStructure);
      });
    });
    root.querySelector("[data-successor-from-feedback]")?.addEventListener("click", () => {
      const feedback = latestChangeRequest();
      const notes = text(feedback?.feedback);
      beginStructureChat(selectedBuildingId || "city_cordon_ring_segmented", {
        successorRevisionId: feedback?.revisionId,
        draft: notes ? `Continue from the rejected version and address these review notes:\n${notes}\n\nRequested change:\n` : "",
      });
    });
    root.querySelector("[data-close-structure-proposal]")?.addEventListener("click", () => {
      setProposalOpen(false);
      clearProposalBranch();
      proposalDraft = "";
      render();
    });
    root.querySelectorAll("[data-start-building-production]").forEach((button) => {
      button.addEventListener("click", () => startBuildingProduction(button.dataset.startBuildingProduction));
    });
    root.querySelector("[data-open-production-review]")?.addEventListener("click", () => navigate?.("inbox"));
    root.querySelector("[data-open-building-assets]")?.addEventListener("click", () => navigate?.("assets"));
    root.querySelector("#building-brief-form")?.addEventListener("submit", (event) => submitChange(event, true));
    root.querySelector("#building-iteration-form")?.addEventListener("submit", (event) => submitChange(event, false));
  }

  function rememberComposer() {
    const field = root.querySelector("#building-session-draft");
    if (!field) return null;
    sessionDraft = field.value;
    return document.activeElement === field ? { start: field.selectionStart, end: field.selectionEnd } : null;
  }

  function restoreComposer(selection) {
    if (!selection || workspaceMode !== "session" || mobilePane !== "conversation") return;
    requestAnimationFrame(() => {
      const field = root.querySelector("#building-session-draft");
      if (!field) return;
      field.focus();
      field.setSelectionRange(Math.min(selection.start, field.value.length), Math.min(selection.end, field.value.length));
    });
  }

  async function fetchConversationState(projectId = getProjectId(), { quiet = false } = {}) {
    if (!projectId) return;
    const selection = quiet ? rememberComposer() : null;
    sessionLoading = !quiet;
    sessionError = "";
    if (!quiet && active) renderWorkingSession();
    try {
      const collection = await api(conversationBase(projectId));
      conversations = Array.isArray(collection.conversations) ? collection.conversations : [];
      if (!selectedConversationId || !conversations.some((item) => item.id === selectedConversationId)) {
        selectedConversationId = conversations[0]?.id || "";
        notifyRouteStateChange();
      }
      if (selectedConversationId) {
        const detail = await api(`${conversationBase(projectId)}/${encodeURIComponent(selectedConversationId)}`);
        conversationDetail = {
          conversation: detail.conversation || conversations.find((item) => item.id === selectedConversationId),
          messages: Array.isArray(detail.messages) ? detail.messages : [],
          sideQuests: Array.isArray(detail.sideQuests) ? detail.sideQuests : [],
          atlas: detail.atlas || null,
          tickets: Array.isArray(detail.tickets) ? detail.tickets : [],
        };
        waitingForReply = activeReplyTicket(conversationDetail.tickets);
      } else conversationDetail = null;
      sessionLoaded = true;
    } catch (error) {
      sessionError = `Working session unavailable: ${String(error)}`;
      if (!quiet) toast(sessionError, true);
    } finally {
      sessionLoading = false;
      if (active && workspaceMode === "session") {
        renderWorkingSession();
        restoreComposer(selection);
        schedulePoll();
      }
    }
  }

  async function openStructureExploration(structure, requestedChange, mode = "fork") {
    const projectId = getProjectId();
    const sourceBriefValue = structureExplorationBrief(requestedChange, structure, {
      projectDescription: response?.project?.description,
      mode,
      baseRevisionId: currentRevision()?.id || "",
      branch: proposalBranch(),
    });
    if (!projectId || !sourceBriefValue || startingSession) return;
    startingSession = true;
    sessionLoading = true;
    sessionError = "";
    sessionDraft = "";
    sessionStartBrief = sourceBriefValue;
    selectedConversationId = "";
    conversationDetail = null;
    sessionLoaded = false;
    setMobilePane("conversation");
    setWorkspaceMode("session");
    renderWorkingSession();
    try {
      const created = await api(conversationBase(projectId), {
        method: "POST",
        body: JSON.stringify({ sourceBrief: sourceBriefValue }),
      });
      selectedConversationId = created.conversation?.id || created.id || "";
      notifyRouteStateChange();
      waitingAfterSequence = created.message?.sequence || 1;
      waitingForReply = true;
      sessionStartBrief = "";
      proposalDraft = "";
      proposalOpen = false;
      await fetchConversationState(projectId, { quiet: true });
      toast("Your exact change was sent to a new Structure agent discussion");
    } catch (error) {
      sessionError = `Could not open agent discussion: ${String(error)}`;
      proposalTargetId = structure?.id || "city_cordon_ring_segmented";
      sessionDraft = requestedChange;
      toast(sessionError, true);
    } finally {
      startingSession = false;
      sessionLoading = false;
      if (active) render();
    }
  }

  async function startStructureChat(event) {
    event.preventDefault();
    const requestedChange = String(new FormData(event.currentTarget).get("content") || "").trim();
    if (!requestedChange) return;
    const structure = structureChatTarget();
    sessionDraft = requestedChange;
    await openStructureExploration(structure, requestedChange, structure?.isCanonicalCordonSegment ? "fork" : "fork");
  }

  async function startConversation(event) {
    event.preventDefault();
    const projectId = getProjectId();
    const sourceBriefValue = String(new FormData(event.currentTarget).get("sourceBrief") || "").trim();
    if (!projectId || !sourceBriefValue || startingSession) return;
    startingSession = true;
    renderWorkingSession();
    try {
      const created = await api(conversationBase(projectId), { method: "POST", body: JSON.stringify({ sourceBrief: sourceBriefValue }) });
      selectedConversationId = created.conversation?.id || created.id || "";
      notifyRouteStateChange();
      sessionStartBrief = "";
      waitingAfterSequence = created.message?.sequence || 1;
      waitingForReply = true;
      sessionLoaded = false;
      await fetchConversationState(projectId, { quiet: true });
      toast("Working session opened");
    } catch (error) {
      sessionError = `Could not open working session: ${String(error)}`;
      toast(sessionError, true);
    } finally {
      startingSession = false;
      if (active) renderWorkingSession();
    }
  }

  async function postSessionMessage(content, references = []) {
    const projectId = getProjectId();
    const id = conversationId();
    const clean = String(content || "").trim();
    if (!projectId || !id || !clean || sendingMessage) return;
    waitingAfterSequence = latestMessageSequence();
    sendingMessage = true;
    try {
      const created = await api(`${conversationBase(projectId)}/${encodeURIComponent(id)}/messages`, {
        method: "POST",
        body: JSON.stringify({ clientMessageId: crypto.randomUUID(), content: clean, references, expectedLastSequence: waitingAfterSequence }),
      });
      waitingAfterSequence = created.message?.sequence || waitingAfterSequence + 1;
      waitingForReply = true;
      sessionDraft = "";
      await fetchConversationState(projectId, { quiet: true });
    } catch (error) {
      toast(`Could not send memo: ${String(error)}`, true);
    } finally {
      sendingMessage = false;
      if (active) renderWorkingSession();
      schedulePoll();
    }
  }

  async function sendComposerMessage(event) {
    event.preventDefault();
    await postSessionMessage(new FormData(event.currentTarget).get("content"));
  }

  async function launchSideQuest(rawSuggestion, sourceMessageId) {
    const projectId = getProjectId();
    const id = conversationId();
    if (!projectId || !id || questActionId) return;
    const resolvedSourceMessageId = rawSuggestion.sourceMessageId || sourceMessageId || "";
    const suggestion = {
      suggestionId: rawSuggestion.suggestionId || rawSuggestion.id,
      title: text(rawSuggestion.title || rawSuggestion.name),
      summary: text(rawSuggestion.summary || rawSuggestion.brief || rawSuggestion.description),
      rationale: text(rawSuggestion.rationale || rawSuggestion.reason || rawSuggestion.summary),
      kind: rawSuggestion.kind || rawSuggestion.type,
      scope: rawSuggestion.scope,
      requestedOutputs: rawSuggestion.requestedOutputs || rawSuggestion.outputs,
    };
    questActionId = `launch:${resolvedSourceMessageId || suggestion.title}`;
    renderWorkingSession();
    try {
      await api(`${conversationBase(projectId)}/${encodeURIComponent(id)}/side-quests`, {
        method: "POST",
        body: JSON.stringify({
          suggestion,
          sourceMessageId: resolvedSourceMessageId,
          clientRequestId: `${resolvedSourceMessageId}:${suggestion.suggestionId}`,
        }),
      });
      toast("Side quest queued");
      await fetchConversationState(projectId, { quiet: true });
    } catch (error) {
      toast(`Could not run side quest: ${String(error)}`, true);
    } finally {
      questActionId = "";
      if (active) renderWorkingSession();
      schedulePoll();
    }
  }

  async function dismissSideQuest(questId) {
    await questMutation(questId, "dismiss", {}, "Side quest dismissed");
  }

  async function acceptSideQuestOption(questId, optionId) {
    await questMutation(questId, "accept", { optionId, expectedBaseRevisionId: atlasBaseRevisionId() || null }, "Direction staged as an Atlas revision");
  }

  async function questMutation(questId, action, body, success) {
    const projectId = getProjectId();
    const id = conversationId();
    if (!projectId || !id || !questId || questActionId) return;
    questActionId = `${action}:${questId}`;
    renderWorkingSession();
    try {
      await api(`${conversationBase(projectId)}/${encodeURIComponent(id)}/side-quests/${encodeURIComponent(questId)}/${action}`, { method: "POST", body: JSON.stringify(body) });
      toast(success);
      await Promise.all([fetchConversationState(projectId, { quiet: true }), fetchPlan(projectId).then((next) => { response = next; }).catch(() => {})]);
    } catch (error) {
      toast(`Could not ${action} side quest: ${String(error)}`, true);
    } finally {
      questActionId = "";
      if (active) renderWorkingSession();
    }
  }

  async function reactToOption(reaction, questId, optionId) {
    const direction = reaction === "more" ? "More like this" : "Less like this";
    await postSessionMessage(`${direction}: side quest option ${optionId}. Record this in the preference ledger and carry the preference into future structure proposals.`, [{ kind: "option", ref: `${questId}:${optionId}`, label: `Side quest option ${optionId}`, relevance: direction, uri: null }]);
  }

  async function submitChange(event, firstPlan) {
    event.preventDefault();
    const projectId = getProjectId();
    const form = event.currentTarget;
    const raw = String(new FormData(form).get("instruction") || "").trim();
    if (!projectId || !raw || queueing) return;
    const targetId = form.dataset.proposalTarget;
    const structure = targetId
      ? currentBuildings().find((item) => item.id === targetId) || planStructures({ radialStructures: [CITY_CORDON_PREVIEW_SPECIFICATION] })[0]
      : null;
    const proposalMode = String(new FormData(form).get("proposalMode") || "fork");
    const instruction = targetId ? structureProposalInstruction(raw, structure, { mode: proposalMode }) : raw;
    const branch = proposalRetryRevisionId
      ? { kind: "retry_parent", reviewedRevisionId: proposalRetryRevisionId }
      : proposalBaseRevisionId
        ? { kind: "continue_rejected_revision", reviewedRevisionId: proposalBaseRevisionId }
        : null;
    queueing = true;
    render();
    try {
      await api(`/projects/${encodeURIComponent(projectId)}/building-plan/changes`, {
        method: "POST",
        body: JSON.stringify({
          instruction,
          baseRevisionId: response?.artifact?.workingRevisionId ?? response?.artifact?.approvedRevisionId ?? null,
          ...(branch ? { branch } : {}),
        }),
      });
      toast(firstPlan ? "Structure atlas queued" : "Structure revision queued");
      form.reset?.();
      proposalDraft = "";
      clearProposalBranch();
      setProposalOpen(false);
      await refresh({ quiet: true });
    } catch (error) {
      toast(`Could not queue structure work: ${String(error)}`, true);
    } finally {
      queueing = false;
      render();
    }
  }

  async function generateConcept(buildingId, kind) {
    const projectId = getProjectId();
    const building = currentBuildings().find((item) => item.id === buildingId);
    const prompt = building ? promptFor(building, kind) : "";
    if (!projectId || !building || !prompt || generatingNote) return;
    const note = `building:${building.id}:${kind}`;
    generatingNote = note;
    renderPlan();
    try {
      const generated = await api("/images/generate", {
        method: "POST",
        body: JSON.stringify({ projectId, prompt, aspectRatio: kind === "cutaway" ? "4:3" : "16:9", resolution: "1K" }),
      });
      imageLibrary = [{ key: generated.key, role: `building_${kind}`, source: "generated", note, prompt, model: generated.model, createdAt: new Date().toISOString() }, ...imageLibrary.filter((image) => image.key !== generated.key)];
      const saved = await api("/images/library", { method: "PUT", body: JSON.stringify({ projectId, images: imageLibrary }) });
      imageLibrary = Array.isArray(saved.images) ? saved.images : imageLibrary;
      toast(`${building.name} ${kind} concept generated`);
    } catch (error) {
      toast(`Could not generate concept: ${String(error)}`, true);
    } finally {
      generatingNote = "";
      renderPlan();
    }
  }

  async function startBuildingProduction(buildingId) {
    const projectId = getProjectId();
    if (!projectId || !buildingId || startingProductionId) return;
    startingProductionId = buildingId;
    renderPlan();
    try {
      const result = await api(`/projects/${encodeURIComponent(projectId)}/building-plan/buildings/${encodeURIComponent(buildingId)}/produce`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast(result.deduplicated ? "Existing Blender build opened" : "Blender build queued from the approved routing plan");
      await refresh({ quiet: true });
    } catch (error) {
      toast(`Could not start Blender production: ${String(error)}`, true);
    } finally {
      startingProductionId = "";
      if (active) render();
    }
  }

  function render() {
    clearTimeout(pollTimer);
    if (loading) return renderLoading();
    if (!getProjectId()) return renderNoProject();
    if (workspaceMode === "session") renderWorkingSession();
    else if (!currentRevision()) renderEmptyPlan();
    else renderPlan();
    schedulePoll();
  }

  async function fetchPlan(projectId, { revisionId = "" } = {}) {
    try {
      const query = revisionId ? `?revisionId=${encodeURIComponent(revisionId)}` : "";
      return await api(`/projects/${encodeURIComponent(projectId)}/building-plan${query}`);
    } catch (error) {
      if (/not found|no building plan/i.test(String(error))) return { project: null, artifact: null, revision: null, revisions: [], tickets: [] };
      throw error;
    }
  }

  async function refresh({ quiet = false } = {}) {
    const projectId = getProjectId();
    if (!projectId) { response = null; imageLibrary = []; return render(); }
    const previousCurrentRevisionId = response?.revision?.id || response?.currentRevision?.id || "";
    loading = !quiet;
    if (!quiet) renderLoading();
    try {
      const [next, library] = await Promise.all([
        fetchPlan(projectId),
        api(`/images/library?projectId=${encodeURIComponent(projectId)}`).catch(() => ({ images: [] })),
      ]);
      response = next;
      imageLibrary = Array.isArray(library.images) ? library.images : [];
      const nextCurrentRevisionId = next.revision?.id || next.currentRevision?.id || next.revisions?.[0]?.id || "";
      const wasFollowingCurrent = !selectedRevisionId || selectedRevisionId === previousCurrentRevisionId;
      if (wasFollowingCurrent || !(next.revisions || []).some((revision) => revision.id === selectedRevisionId)) {
        setSelectedRevisionId(nextCurrentRevisionId);
      }
      if (workspaceMode === "session" && !sessionLoaded) await fetchConversationState(projectId, { quiet: true });
    } catch (error) {
      toast(`Structure atlas unavailable: ${String(error)}`, true);
      response = { revision: null, revisions: [], tickets: [] };
    } finally {
      loading = false;
      if (active) render();
    }
  }

  function schedulePoll() {
    clearTimeout(pollTimer);
    if (!active) return;
    if (workspaceMode === "session" && (waitingForReply || runningSideQuests().length)) {
      pollTimer = window.setTimeout(() => fetchConversationState(getProjectId(), { quiet: true }), 3500);
      return;
    }
    const productionActive = (response?.productionTickets || []).some((ticket) => ACTIVE_TICKET_STATUSES.has(ticket.status));
    if (activeTicket() || productionActive) pollTimer = window.setTimeout(() => refresh({ quiet: true }), 6000);
  }

  function projectChanged() {
    response = null;
    imageLibrary = [];
    setSelectedBuildingId("");
    setSelectedRevisionId("");
    conversations = [];
    conversationDetail = null;
    selectedConversationId = "";
    sessionLoaded = false;
    sessionError = "";
    sessionDraft = "";
    sessionStartBrief = "";
    proposalDraft = "";
    clearProposalBranch();
    proposalOpen = false;
    proposalTargetId = "";
    waitingForReply = false;
    waitingAfterSequence = 0;
    keptOptionIds.clear();
    clearTimeout(pollTimer);
    if (active) refresh();
  }

  function setActive(value) {
    active = Boolean(value);
    if (!active) clearTimeout(pollTimer);
  }

  async function applyRouteState({ workspace, revisionId, structureId, conversationId: routeConversationId, pane, successorRevisionId, retryRevisionId } = {}) {
    let changed = false;
    if (workspace != null) changed = setWorkspaceMode(workspace) || changed;
    if (revisionId != null) changed = setSelectedRevisionId(revisionId) || changed;
    if (structureId != null) changed = setSelectedBuildingId(structureId) || changed;
    if (routeConversationId != null) {
      const nextConversationId = String(routeConversationId || "");
      if (selectedConversationId !== nextConversationId) {
        selectedConversationId = nextConversationId;
        conversationDetail = null;
        sessionLoaded = false;
        changed = true;
      }
    }
    if (pane === "proposal" || pane === "chat") {
      proposalTargetId = structureId || proposalTargetId || "city_cordon_ring_segmented";
      selectedConversationId = "";
      conversationDetail = null;
      sessionLoaded = true;
      changed = setWorkspaceMode("session") || changed;
      changed = setMobilePane("conversation") || changed;
      if (successorRevisionId == null && retryRevisionId == null) changed = clearProposalBranch() || changed;
    }
    else if (pane != null) {
      changed = setProposalOpen(false) || changed;
      changed = clearProposalBranch() || changed;
      if (pane) changed = setMobilePane(pane) || changed;
    }
    if (successorRevisionId != null || retryRevisionId != null) {
      changed = setProposalBranch({
        successorRevisionId: successorRevisionId != null ? successorRevisionId : proposalBaseRevisionId,
        retryRevisionId: retryRevisionId != null ? retryRevisionId : proposalRetryRevisionId,
      }) || changed;
    }

    const projectId = getProjectId();
    if (projectId && !response && (revisionId || structureId)) response = await fetchPlan(projectId);
    if (workspaceMode === "session" && projectId && !sessionLoaded) await fetchConversationState(projectId, { quiet: true });
    if (active && changed) render();
    return getRouteState();
  }

  async function resolveStructure(structureId, { revisionId = "" } = {}) {
    const id = String(structureId || "");
    const projectId = getProjectId();
    if (!id || !projectId) return null;
    const loaded = response?.revision?.id === revisionId || response?.revisions?.some((item) => item.id === revisionId);
    if (!response || (revisionId && !loaded)) response = await fetchPlan(projectId, { revisionId });
    const revisions = Array.isArray(response?.revisions) ? response.revisions : [];
    const revision = revisionId
      ? revisions.find((item) => item.id === revisionId) || (response?.revision?.id === revisionId ? response.revision : null)
      : currentRevision();
    return resolveStructureFromBody(revision?.body || {}, id, { allowCatalogFallback: !revisionId });
  }

  async function resolveStructurePreview(structureId, { revisionId = "", source = false } = {}) {
    const id = String(structureId || "");
    const projectId = getProjectId();
    if (!id || !projectId) return null;
    const loaded = response?.revision?.id === revisionId || response?.revisions?.some((item) => item.id === revisionId);
    if (!response || (revisionId && !loaded)) response = await fetchPlan(projectId, { revisionId });
    return buildStructurePreviewContext(response, id, { revisionId, source });
  }

  return { render: () => refresh(), refresh, projectChanged, setActive, getRouteState, applyRouteState, resolveStructure, resolveStructurePreview };
}

function isUnmodifiedPrimaryLinkClick(event) {
  return event.button === 0
    && !event.defaultPrevented
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;
}

// Compatibility alias: existing callers, URLs, CSS, and local preferences keep
// their stable identifiers while the product surface is Structures-first.
export const initBuildings = initStructures;
